/**
 * AuthoringRail — ADR-166 D7 and ADR-167 D6: the phase state of an authoring run,
 * while it is running.
 *
 * WHAT THIS REPLACES, in the owner's words: a run that reports "working on it — 1016s
 * elapsed" is not observable; a run that reports "phase 4 of 6; design gate passed; 3 of
 * 7 tests authored and failing as required" is. Elapsed time is the only thing the
 * editor has ever shown, and at 1016s it cannot separate working from looping from
 * stuck.
 *
 * THREE THINGS THIS MUST NOT DO, each a defect already shipped once on this product:
 *
 *  - render "no run" out of "we could not read it". The backend answers
 *    `not_configured` separately for exactly this reason, and collapsing the two is the
 *    RefChecksStatus failure that showed "waiting for checks to start" over a red PR.
 *  - show a REFUSED gate without its reason. A refusal with no reason turns an
 *    observable process back into a transcript, which is what D7 exists to prevent.
 *  - show a pending checkpoint with no way to answer it. A checkpoint nobody can decide
 *    is strictly worse than the timeout it replaced, because at least a timeout returns.
 *  - show a run with no way back to empty. A run is keyed (agent, workflow, operator) and
 *    otherwise lives forever, so a finished or wedged run is what every later turn merges
 *    into. Observed on spartan's weekly-health: a 1200s fleet-architect timeout left
 *    "Waiting on you at analysis — 6 items routed for review" on the Process tab for
 *    HOURS, and the operator had no control that would clear it. D8 calls a state with no
 *    action a design bug; this was one.
 */
import type React from 'react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { phaseActivityNote, runGuidance, runStoppedBy, turnElapsed } from './authoringRunGuidance'
import { useAttentionNotify } from '../context/AttentionContext'
import { proposalIsSettled, useImprovementStatus } from '../pages/useAwaitingProposal'
import { verdictClass } from '../utils/verdictClass'

import { api, type RegistrationState } from '../api/client'

/** The seven phases, in order (ADR-166 D2). Mirrors PhaseOrder in the backend. */
export const PHASE_ORDER = [
  'analysis',
  'plan',
  'design',
  'test-authoring',
  'implementation',
  'results',
  'done',
] as const

export type Phase = (typeof PHASE_ORDER)[number] | string

/**
 * Phase names compare on a normalised form, because the two sides spell one of them
 * differently.
 *
 * PHASE_ORDER above says `test-authoring`; the Go constant is
 * `PhaseTestAuthoring Phase = "test_authoring"`, and that underscore is what lands in
 * every run document, verdict and turn. So a straight === matched five phases out of six
 * and silently missed the sixth — the run tree rendered "no phase recorded" over a
 * test-authoring turn that had run for 33 seconds and authored a test.
 *
 * Normalising rather than renaming either side: the Go constant is serialised into every
 * run record already written and into the ADR-166 git branch, and the hyphenated form is
 * what the rail has always displayed.
 */
export function samePhase(a: Phase | undefined, b: Phase | undefined): boolean {
  if (a === undefined || b === undefined) return false
  return String(a).replace(/_/g, '-') === String(b).replace(/_/g, '-')
}

export interface TransitionVerdict {
  turn_seq?: number
  from: Phase
  to: Phase
  passed: boolean
  reason?: string
  /** The stable identity of a refusal (ADR-175). Grouping on `reason` means
   *  substring-matching English that any edit silently re-partitions. */
  code?: string
  route_to?: Phase
  decided_at: string
}

export interface OpenItem {
  item: string
  confidence?: number
  routed_for_review?: boolean
  review_question?: string
  kind?: string
  /** What SHAPE of answer this item wants, so the card offers a control rather than a
   *  text box (ADR-181 D4 rule 2). Set by Mission Control, never by the turn. Absent or
   *  unrecognised means free text, which is the stated fallback and must stay reachable. */
  answer_kind?: 'data_set' | 'decision' | string
  /** ADR-200 stage 1 — a scoping question's identity (`BriefQuestion.ID`), carried on the
   *  OpenItem MC builds for a `scoping` checkpoint (`Kind:"brief-question"`). Absent for
   *  every other checkpoint reason: those items are not answered by id, they are answered
   *  as a single free-text `answer` (ADR-181 D6b). */
  id?: string
  /** The proposed default (D3): "most answers are one click." Pre-fills the control; an
   *  unedited box is how the operator ACCEPTS it, and the card sends "" for that case to
   *  match the backend's `answers` contract. */
  default?: string
  /** A closed vocabulary for this question, when one exists (ADR-179 pick-don't-describe,
   *  ADR-181 D4 rule 2). Absent means free text is the only fit. */
  options?: string[]
}

export interface Checkpoint {
  reason: string
  at_phase: Phase
  elapsed_s?: number
  done?: string[]
  open?: OpenItem[]
  stuck_on?: string
  decision: string
  redirect?: string
  decided_by?: string
  continued_over_review?: string[]
  /** ADR-200 D7 — set by Mission Control on a scoping checkpoint that is AMENDING an agreed
   *  brief rather than agreeing a first one: the id of the BriefAmendment it is about. */
  amendment_id?: string
  /** When Mission Control opened this checkpoint (ADR-200 D6). */
  opened_at?: string
  /** The fence this checkpoint offers to lift: either the request prohibition on these
   *  paths, or the measured published tests that may be removed or disabled. */
  omits?: string[]
  fence_lift?: {
    id: string
    paths: string[]
    kind?: string
    removed_tests?: string[]
    disabled_tests?: string[]
  }
  task_replan?: {
    id: string
    tasks: string[]
    evidence?: { task: string; failures: { test: string; message: string }[] }[]
    changeset_hash?: string
  }
  replan_tasks?: string[]
  /** A persisted re-review currently claiming this not-reviewed checkpoint. */
  re_review?: {
    id: string
    by?: string
    started_at: string
    stale_at?: string
    turn_count?: number
  }
}

function reReviewIsFresh(cp: Checkpoint, now = Date.now()): boolean {
  if (cp.reason !== 'not-reviewed' || !cp.re_review?.stale_at) return false
  const staleAt = Date.parse(cp.re_review.stale_at)
  return Number.isFinite(staleAt) && staleAt > now
}

/**
 * ADR-200 stage 1 — the confirmed contract the gates read, not the prose. Mirrors the Go
 * `Brief` (`internal/handler/authoring_brief.go`) field for field; JSON is snake_case on
 * both sides, so this is a straight transcription rather than a remapping.
 */
export interface BriefTarget {
  file: string
  name?: string
}

export interface BriefTrigger {
  kind: string
  detail?: string
}

export interface BriefInput {
  name: string
  type?: string
  default?: string
  meaning?: string
}

export interface BriefReturn {
  name: string
  type?: string
  meaning?: string
}

export interface BriefSideEffect {
  tool: string
  what?: string
  /** A `null`/absent default reads as "not stated", not as false — a card that renders
   *  the absence as "off" would be inventing a fact the agent never gave a source for. */
  dry_run_default?: boolean | null
}

export interface BriefUnchanged {
  file: string
  tasks?: string[]
  whole_file?: boolean
}

export interface BriefTaskChange {
  file: string
  task: string
  operation: 'create' | 'modify' | string
}

export interface BriefTests {
  files?: string[]
  new_test_file_allowed?: boolean | null
}

export interface BriefEnvelope {
  verdict: 'in' | 'split' | 'decline' | string
  reason?: string
  split?: string[]
}

export interface Brief {
  kind: 'create' | 'edit' | string
  target: BriefTarget
  goal: string
  trigger?: BriefTrigger
  inputs?: BriefInput[]
  returns?: BriefReturn[]
  side_effects?: BriefSideEffect[]
  unchanged?: BriefUnchanged[]
  task_changes?: BriefTaskChange[]
  tests?: BriefTests
  acceptance?: string[]
  out_of_scope?: string[]
  envelope: BriefEnvelope
  /** field path -> source. Mandatory on the Go side for every filled field (D2); optional
   *  here because a draft mid-scoping may still be missing one, which is exactly what a
   *  question is for. */
  sources?: Record<string, string>
}

/** One open question MC could not answer from the request or the repo (D3), before it is
 *  folded into a checkpoint's `open` as an `OpenItem{Kind:"brief-question"}`. */
export interface BriefQuestion {
  id: string
  field: string
  question: string
  why?: string
  default?: string
  options?: string[]
}

/** One answer the operator gave, across however many scoping rounds it took (D3, capped
 *  at two). */
export interface BriefAnswer {
  question_id: string
  question?: string
  answer: string
  answered_by?: string
  round?: number
  accepted_default?: boolean
}

/**
 * One turn the run made (ADR-166 D10, ADR-177 D2).
 *
 * `turns` is EVERY attempt including the refused ones, which is what makes the run a tree
 * rather than a transcript: a phase that converged on attempt 3 keeps the two refusals
 * that explain it. `turn` alone was a single value each attempt overwrote, and on
 * 2026-08-31 that made three production failures unreviewable.
 */
export interface TurnFacts {
  turn_seq?: number
  phase?: Phase
  started_at?: string
  attempt?: number
  duration_s?: number
  model?: string
  mc_version?: string
  reply_chars?: number
  engine?: string
  error?: string
  /** ADR-199 D7 — the agent session that authored this attempt, when the engine was one. */
  agent?: AgentTurnFacts
}

/** ADR-199 D7 — what an agent author's session did on one attempt. */
export interface AgentTurnFacts {
  engine: string
  session_id?: string
  resumed?: boolean
  invocations: number
  num_turns: number
  cost_usd: number
  denied: number
  rail_checks: number
  duration_s: number
  timed_out?: boolean
  dropped_paths?: string[]
  deleted_paths?: string[]
  artifact_missing?: boolean
}

export interface AuthoringRunState {
  run_id?: string
  legacy_turns?: number
  legacy_verdicts?: number
  scope: { scope: string; reason: string; tasks_changed?: string[]; collapse_phases: boolean }
  phase: Phase
  /** A turn is running against this slot right now (ADR-166 D7). */
  turn_in_flight?: boolean
  /** A worker attempt is running, including D3 retries that are not a whole turn. */
  active_attempt?: { job_id?: string; phase?: Phase; started_at?: string }
  /** A DETERMINISTIC MEASUREMENT IN PROGRESS, distinct from a model turn (ADR-166).
   *
   *  The `results` phase reverts each changed file and re-runs the whole fleet gate per
   *  file, so its cost is N suite runs — not one model call. It used to share
   *  `turn_in_flight`, so the rail could only say "Working on results…" for 56 minutes
   *  (spartan 20260916T234753Z-281452) with no way to tell working from wedged. */
  measuring?: {
    phase?: Phase
    kind?: string
    started_at?: string
    updated_at?: string
    to_prove?: string[]
    proven?: string[]
    running?: string
    skipped?: string[]
  }
  turn_started_at?: string
  artifacts: {
    claims?: {
      id?: string; text: string; citation?: string
      used_as_constraint?: boolean; blocking?: boolean
    }[]
    plan?: { summary: string; traces_to?: string; files?: string[]; outcome?: string }[]
    design?: { file: string; symbols?: string[]; unresolved_symbols?: string[] }[]
    tests?: { path: string; fails_against_current_tree?: boolean; inconclusive?: boolean }[]
    changed_files?: string[]
    tests_now_pass?: boolean
    revert_proves_red?: boolean
    revert_not_applicable?: boolean
    measurement_detail?: string
  }
  /** The files this run has authored so far, contents included.
   *
   *  CAPITALISED KEYS, and it is not a typo: the Go `AuthoredFile` carries no json tags,
   *  so it serialises as `Path`/`Content`. Both spellings are declared because a reader
   *  who guesses the lowercase one gets `undefined` and every authored file renders as
   *  empty — silently. `Deleted` is the same rule: a REMOVAL rather than a write, Content
   *  always empty when it is true. */
  changeset?: {
    Path?: string; Content?: string; Deleted?: boolean
    path?: string; content?: string; deleted?: boolean
  }[]
  verdicts?: TransitionVerdict[]
  turns?: TurnFacts[]
  checkpoints?: Checkpoint[]
  /** ADR-178 D1/D5 — the run's self-driving state. The deadline is the SERVER's; this is
   *  read, never computed here. */
  auto_advance?: {
    mode?: 'auto' | 'paused'
    next_turn_at?: string
    armed_for_phase?: Phase
    controls?: { action: string; by: string; at: string }[]
  }
  /** ADR-178 D3 — what this run proposed once it reached `done`. An `error` inside means it
   *  TRIED and failed, which must not read as completion. */
  proposal?: { pr?: number; url?: string; error?: string; provenance_note?: string; at?: string; improvement_id?: string
               improvement_warning?: string; registration_state?: RegistrationState }
  /** Why the run stopped driving itself — ADR-178's `redrive_exit`, e.g. "stopped after 6
   *  attempt(s): every attempt was refused".
   *
   *  It has been on the wire since D2's retry cap and was never declared here, so every
   *  reader got `undefined` and the surface had nothing to say about a run that had given
   *  up. That is the "no recovery, no guidance" state an operator hit on
   *  analytics/daily-check-report. */
  redrive_exit?: string
  /** ADR-200 amendment (2026-09-29) — whether this run scopes: the operator's per-run
   *  choice, recorded once when the run opened and never changed after. Absent means no. */
  scoping_enabled?: boolean
  /** ADR-200 stage 1 — the draft or confirmed brief. Present as soon as the scoping
   *  session writes a valid `.rail/brief.json`; still present, unchanged, after
   *  confirmation — `brief_confirmed_at` is what flips draft into agreed. */
  brief?: Brief
  /** This round's open questions (D3) — the same list the pending checkpoint's `open`
   *  carries as `OpenItem`s, kept here too so a card can read the round's shape without
   *  the checkpoint object, e.g. after it has been decided. */
  brief_questions?: BriefQuestion[]
  /** Every answer given across every round so far, oldest first. */
  brief_answers?: BriefAnswer[]
  /** Which scoping round the run is on, 1-indexed. Capped at 2 (D3) — a third would-be
   *  round is the round-cap checkpoint instead. */
  brief_round?: number
  /** Set the moment a human confirms (D6) — never by the agent. Its presence is what
   *  lets the rail show the agreed brief read-only instead of the editable draft. */
  brief_confirmed_at?: string
  /** The token that confirmed it — operator or fleet_owner only (D6). */
  brief_confirmed_by?: string
  /** ADR-200 D6/D7 — where the brief in force came from: "scoping", "stored:<id>" (a stored
   *  brief an unattended start referenced) or "amendment:<id>". */
  brief_source?: string
  /** ADR-200 D7 — every brief a person confirmed on this run, oldest first: brief@1, brief@2… */
  brief_versions?: BriefVersion[]
  /** ADR-200 D7 — every time a later phase found the brief wrong and routed back to scoping. */
  brief_amendments?: BriefAmendment[]
}

export const ATTENTION_CHECKPOINT_REASONS = ['needs-information', 'scoping', 'timebox', 'hard_timeout', 'spend-cap', 'not-reviewed'] as const

export function checkpointAttentionId(run: AuthoringRunState | null): string | null {
  if (!run) return null
  const index = (run.checkpoints ?? []).findIndex(cp => !cp.decision || cp.decision === 'pending')
  if (index < 0) return null
  const cp = run.checkpoints![index]
  if (!(ATTENTION_CHECKPOINT_REASONS as readonly string[]).includes(cp.reason)) return null
  return `authoring-checkpoint:${run.run_id ?? ''}:${index}:${cp.reason}:${cp.at_phase}:${cp.opened_at ?? ''}:${cp.amendment_id ?? ''}:${cp.reason === 'scoping' ? run.brief_round ?? 0 : ''}`
}

/** One confirmed brief — brief@version — with what changed and when (ADR-200 D7). */
export interface BriefVersion {
  version: number
  brief: Brief
  source: string
  changed?: string[]
  confirmed_by: string
  confirmed_at: string
  amendment_id?: string
  resume_from?: Phase
}

/** A later phase's finding that the agreed brief is wrong or insufficient (ADR-200 D7). */
export interface BriefAmendment {
  id: string
  field: string
  problem?: string
  question: string
  raised_at_phase: Phase
  raised_at: string
  from_version: number
  to_version?: number
  resolved_at?: string
  resumed_at?: Phase
  outcome?: string
}

/** A version as a person reads it: "brief@2 · confirmed by x · changed tests.files · resumed
 *  from test authoring". Pure so the wording is tested without a DOM. */
export function briefVersionLine(v: BriefVersion): string {
  const parts = [`brief@${v.version}`]
  if (v.confirmed_by) parts.push(`confirmed by ${v.confirmed_by}`)
  if (v.source?.startsWith('stored:')) parts.push('from a stored brief')
  if (v.source?.startsWith('amendment:')) parts.push('amended')
  if (v.changed && v.changed.length > 0) parts.push(`changed ${v.changed.join(', ')}`)
  if (v.resume_from) parts.push(`resumed from ${String(v.resume_from).replace(/_/g, ' ')}`)
  return parts.join(' · ')
}

/** The amendment a scoping checkpoint is about, or undefined for a first scoping. */
export function amendmentFor(cp: Checkpoint, run: AuthoringRunState): BriefAmendment | undefined {
  if (!cp.amendment_id) return undefined
  return (run.brief_amendments ?? []).find(a => a.id === cp.amendment_id)
}

/**
 * Seconds remaining before Mission Control takes the next turn, or null when nothing is
 * counting down.
 *
 * PURE, AND TAKES `now`. The countdown is the one thing on this surface that changes every
 * second, and a test that had to wait for a real clock would be the flakiest test in a suite
 * already documented as flaky under parallelism. So the arithmetic is separated from the
 * ticking, exactly as the backend separates AutoAdvanceDue from its scheduler.
 *
 * NEVER NEGATIVE. A deadline in the past means the turn is due or already dispatched, and
 * rendering "-4s" would read as a fault rather than as "any moment now".
 */
export function autoAdvanceSecondsLeft(run: AuthoringRunState | null, now: Date): number | null {
  const at = run?.auto_advance?.next_turn_at
  if (!at || run?.auto_advance?.mode === 'paused') return null
  const due = Date.parse(at)
  if (Number.isNaN(due)) return null
  return Math.max(0, Math.ceil((due - now.getTime()) / 1000))
}

export function railPollsWhileHidden(run: AuthoringRunState | null, now: Date, continuing: boolean): boolean {
  if (continuing) return true
  return ['running', 'counting'].includes(autoAdvanceStatus(run, now).kind)
}

/**
 * What the pacing control should say about this run.
 *
 * A STATE, NOT A SPINNER — ADR-167's standing requirement, and the reason a bare countdown
 * is not enough. Four of these five answers are things a countdown cannot express, and each
 * one leads an operator somewhere different: paused wants Resume, a run mid-turn wants
 * patience, a finished run wants neither, and a run that is waiting on a checkpoint is
 * waiting on THEM.
 */
/** The one line a running measurement is worth, or '' when none is running.
 *
 *  COUNTS, AND THE FILE BEING PROVEN. The denominator is fixed by Mission Control before the
 *  first gate run so it cannot move while a person watches, and the numerator advances per
 *  file — which is what makes "working" distinguishable from "wedged". */
/** Did the run's LAST gate pass?
 *
 *  The propose gate's own condition, mirrored so the button is not offered where the backend
 *  would refuse it: `requestProposal` answers 400 for a run whose last verdict is a refusal,
 *  and a control that answers 400 is worse than no control. No verdicts at all reads as NOT
 *  passed — a run with nothing measured has earned nothing. */
export function lastVerdictPassed(run: AuthoringRunState | null): boolean {
  const vs = run?.verdicts ?? []
  if (vs.length === 0) return false
  return !!vs[vs.length - 1].passed
}

export function measurementProgress(run: AuthoringRunState | null): string {
  const m = run?.measuring
  if (!m || !m.to_prove?.length) return ''
  const done = m.proven?.length ?? 0
  let text = `Proving the change is load-bearing — ${done} of ${m.to_prove.length} file(s) done`
  if (m.running) text += ` · running the whole test suite without ${m.running}`
  if (m.skipped?.length) text += ` · ${m.skipped.length} needed no proof (documentation)`
  return text
}

export function autoAdvanceStatus(run: AuthoringRunState | null, now: Date,
  proposalStatus?: string | null):
  { kind: 'counting' | 'paused' | 'running' | 'blocked' | 'finished'; text: string; seconds?: number } {
  if (!run) return { kind: 'finished', text: 'No run on this slot' }
  // A STOP OUTRANKS EVERYTHING. The stop is a DECIDED
  // checkpoint, so it fell through to `paused` and offered Next and Resume on a run that
  // takes no more turns; read from the checkpoint like the backend's stoppedAt().
  const stopped = runStoppedBy(run)
  if (stopped) {
    return { kind: 'finished', text: stopped.cancelled
      ? 'Cancelled — start a new run to continue'
      : `Stopped by ${stopped.by} at ${stopped.phase} — start a new run to continue` }
  }
  if (run.phase === 'done') {
    // THE PROPOSAL IS THE HEADLINE HERE, because ADR-178 D3 makes `done` mean proposed and
    // a finished run with no artifact is the false-done shape the decision removes.
    if (run.proposal?.error) return { kind: 'finished', text: `Finished — the propose FAILED: ${run.proposal.error}` }
    if (run.proposal?.registration_state === 'pending' && !proposalStatus) {
      return { kind: 'finished', text: 'Proposed — preparing for review' }
    }
    if (run.proposal?.registration_state === 'blocked') {
      return { kind: 'finished', text: 'Proposed — with a problem' }
    }
    // A REGISTRATION THAT FAILED IS NOT A CLEAN PROPOSAL. The warning may come from an older
    // backend without registration_state, so it still receives the problem headline.
    if (run.proposal?.improvement_warning && !proposalStatus) return { kind: 'finished', text: 'Proposed — with a problem' }
    // AND WHAT THE PROPOSAL IS NOW, not what it was when it opened. The fleet owner
    // approved PR #1184 and deployed it, and this line still said "Proposed" over a run
    // still badged CURRENT: "Once again need to stay sync'd and reflect if anything it is
    // merged and deployed." `proposalStatus` is the improvements list's own answer, and an
    // UNKNOWN status keeps the old wording rather than inventing a newer one.
    if (run.proposal?.pr) {
      const settled = proposalStatus && proposalIsSettled(proposalStatus)
      const word = settled
        ? proposalStatus.charAt(0).toUpperCase() + proposalStatus.slice(1)
        : 'Proposed'
      return { kind: 'finished', text: `${word} — PR #${run.proposal.pr}` }
    }
    return { kind: 'finished', text: 'Finished' }
  }
  const pending = (run.checkpoints ?? []).find(c => !c.decision || c.decision === 'pending')
  if (pending) return { kind: 'blocked', text: `Waiting on you at ${pending.at_phase}` }
  // A MEASUREMENT REPORTS ITS COUNT, not a spinner.
  //
  // BEFORE turn_in_flight, because the two are never both set and this one can say something
  // specific: "1 of 2 file(s) done" answers "is this moving?" and "Working on results…"
  // cannot. Reported against the live build on 20260916T234753Z-281452, which sat on that
  // sentence for 56 minutes: "I don't like that it is a black box and not showing iterative
  // results."
  if (measurementProgress(run)) {
    return { kind: 'running', text: measurementProgress(run) as string }
  }
  if (run.turn_in_flight) return { kind: 'running', text: `Working on ${run.phase}…` }
  if (run.auto_advance?.mode === 'paused') {
    return { kind: 'paused', text: `Paused at ${run.phase} — Resume to continue` }
  }
  const left = autoAdvanceSecondsLeft(run, now)
  if (left === null) return { kind: 'paused', text: `Holding at ${run.phase}` }
  return { kind: 'counting', seconds: left,
    text: left === 0 ? `Starting the next phase…` : `Next phase in ${left}s` }
}

/**
 * The phases this run will actually pass through.
 *
 * At node scope analysis/plan/design are ONE artifact (D1), so rendering six separate
 * steps would show three the run never enters — and a rail with permanently-grey steps
 * reads as a stalled run rather than a cheap one.
 */
export function visiblePhases(collapse: boolean): Phase[] {
  if (!collapse) return [...PHASE_ORDER]
  return ['analysis', 'test-authoring', 'implementation', 'results', 'done']
}

/**
 * Which control answers this question — ADR-181 D4 rule 2.
 *
 * TYPED TO THE QUESTION, AND FREE TEXT ONLY WHERE NOTHING FITS. ADR-179's opening
 * complaint is the same instinct one level down: *"I would provide enhanced UI of pickers
 * to let them select from the data. This question is just too ambiguous."* A card that asks
 * an operator to TYPE a data set name recreates exactly the guessing the rail is trying to
 * exit — they type `properties@v1`, the validator refuses it, and the loop resumes with a
 * human inside it.
 *
 * The FIRST recognised kind among the routed items decides, because one card takes one
 * answer. A mixed card is a real possibility and it is not silently flattened: the caller
 * renders the question list above this, so the operator can see both, and `decision`/free
 * text is the safe shape for an answer covering more than one item.
 */
export function answerControlKind(items: OpenItem[]): 'data_set' | 'text' {
  for (const o of items) {
    if (o.answer_kind === 'data_set') return 'data_set'
  }
  return 'text'
}

/**
 * The answer affordance for a needs-information checkpoint.
 *
 * `data_set` picks from the ADR-127 vocabulary — the published sets this fleet actually
 * has — so the value cannot be one the validator will reject. Everything else is a text
 * box, deliberately: a picker over the wrong vocabulary invites a valid-looking answer to
 * a different question, and no downstream check can catch that because the value IS valid.
 *
 * DEGRADES TO TEXT, NEVER TO NOTHING. If the sets cannot be read — no python3, no rules
 * script, a fleet with no data area — the list arrives empty with a note, and this falls
 * back to the text box with that note shown. An unanswerable card is the failure being
 * fixed; it must not be reintroduced by the fix.
 */
function AnswerControl({
  items, busy, onAnswer,
}: {
  items: OpenItem[]
  busy: boolean
  onAnswer: (answer: string) => void
}) {
  const kind = answerControlKind(items)
  const [text, setText] = useState('')
  const [sets, setSets] = useState<string[]>([])
  const [note, setNote] = useState('')

  useEffect(() => {
    if (kind !== 'data_set') return
    let live = true
    void api.getDataSets()
      .then(r => {
        if (!live) return
        setSets((r.sets || []).map(s => s.set).filter(Boolean))
        // A NOTE IS NOT AN ERROR AND NOT AN EMPTY LIST. "We could not ask" and "there are
        // none" are different facts, and the endpoint separates them on purpose.
        if (r.note) setNote(r.note)
      })
      .catch(e => { if (live) setNote(e instanceof Error ? e.message : 'the data sets could not be read') })
    return () => { live = false }
  }, [kind])

  const canPick = kind === 'data_set' && sets.length > 0
  return (
    <div className="space-y-1 rounded border border-amber-200 bg-white px-2 py-1">
      <div className="text-[11px] font-medium text-gray-700">Your answer</div>
      {canPick ? (
        <select
          value={text}
          onChange={e => setText(e.target.value)}
          className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
        >
          <option value="">Pick a published data set…</option>
          {sets.map(s => <option key={s} value={s}>{s}</option>)}
        </select>
      ) : (
        <input
          type="text"
          value={text}
          onChange={e => setText(e.target.value)}
          placeholder="Answer the question above"
          className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
        />
      )}
      {note && kind === 'data_set' && (
        <div className="text-[11px] text-gray-500">
          The set list could not be read ({note}) — type the answer instead.
        </div>
      )}
      <div className="flex flex-wrap gap-1">
        <button
          type="button" disabled={busy || text.trim() === ''}
          onClick={() => onAnswer(text.trim())}
          className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
          title={text.trim() === ''
            ? 'Supply the fact first — an empty answer settles nothing.'
            : 'Answer. The fact becomes evidence the next turn can cite, with you as its source.'}
        >
          Answer
        </button>
        {/* SKIP IS A FIRST-CLASS ANSWER (ADR-181 D4 rule 3), and it writes the deferral the
            model will not write for itself — a turn does not defer the thing it has just
            called blocking. Sent as an ANSWER rather than a continue so the run records
            that the premise was deliberately left unmet, by a person, rather than looking
            like one nobody noticed. */}
        <button
          type="button" disabled={busy}
          onClick={() => onAnswer('skip that')}
          className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
          title="Do the rest of the work and leave this one alone. The run records that you chose to."
        >
          Skip this and carry on
        </button>
      </div>
    </div>
  )
}

/** The stuck_on openers of the hard_timeout checkpoints Continue resumes in a fresh job
 *  (#3168). Mirrored by TestResumableHardTimeoutOpeners_MatchTheBackend in Go. */
export const resumableHardTimeoutOpeners = [
  'not enough budget left to start another agent session',
  'the turn produced no checkpoint before its budget ran out',
] as const

export function hardTimeoutResumes(cp: Pick<Checkpoint, 'reason' | 'stuck_on'>): boolean {
  const stuckOn = (cp.stuck_on ?? '').trimStart()
  return cp.reason === 'hard_timeout'
    && resumableHardTimeoutOpeners.some(opener => stuckOn.startsWith(opener))
}

/**
 * What the card says happened, keyed on the checkpoint's REASON.
 *
 * THERE ARE THREE REASONS AND THE CARD USED TO KNOW TWO. Anything that was not
 * `hard_timeout` read "This phase stopped at its timebox and is waiting on you" — and once
 * ADR-181 D2 lets the ANALYSIS gate stop and ask on discovery, that sentence is simply
 * false: such a run stops after one turn and about thirty seconds, having spent no budget
 * at all. Telling the operator it ran out of time is the "surface disagreeing with the
 * run's own state" defect this rail has already paid for twice, and here it would hide the
 * one fact that makes answering urgent — that answering NOW is cheap.
 *
 * `needs-information` covers both the turn's own D6a question and the gate's D2 one. That
 * is deliberate: to the person reading the card they are the same event ("it needs
 * something from me"), and the run record already distinguishes the two by the verdict that
 * opened it.
 */
export function checkpointHeadline(reason?: string, stuckOn?: string): string {
  // A hard_timeout opened because another agent session could not FIT in the job's remaining
  // time is not a phase that ran out of budget mid-turn (#3123). The backend opens stuck_on
  // with this marker; "ran out of its budget" would contradict it.
  if (reason === 'hard_timeout' && (stuckOn ?? '').startsWith(resumableHardTimeoutOpeners[0])) {
    return 'Too little job time was left to start another agent session, so the run paused here.'
  }
  switch (reason) {
    case 'hard_timeout':
      return 'This phase ran out of its budget before reporting.'
    case 'spend-cap':
      return 'This run reached its spend cap.'
    case 'not-reviewed':
      return 'The review of this phase could not run — confirm to re-review.'
    case 'needs-information':
      return 'This run has stopped to ask you something.'
    // ADR-200 stage 1 — kept here too, so any caller that reads a checkpoint generically
    // (the run history, an older build of this same card) still says something true
    // rather than falling into the timebox sentence below, which is specifically wrong
    // for scoping: analysis.brief_unconfirmed is a wait for a PERSON, not a budget.
    case 'scoping':
      return "Before I build this, let's agree what it is."
    default:
      return 'This phase stopped at its timebox and is waiting on you.'
  }
}

/**
 * ADR-200 D2/D3 — how a `sources[...]` value reads to an operator, never the raw string.
 *
 * The brief's own contract is precise on purpose ("a quote of 8 or more characters",
 * "a file that must exist in the run's seed tree") because a GATE reads it; a person
 * reading the card does not need the mechanism, only which of four things grounded the
 * field: their own words, the repo, an answer they gave, or a default they let stand.
 */
export function briefSourceLabel(source?: string): string {
  if (!source) return ''
  if (source.startsWith('operator:')) return 'your answer'
  if (source.startsWith('default:')) return 'default you accepted'
  if (source.startsWith('request:')) return 'from your request'
  if (source.startsWith('repo:')) {
    // repo:<path>[:<line>] — the line is precision for the gate, not for this card.
    const rest = source.slice('repo:'.length)
    const path = rest.split(':')[0]
    return path ? `from ${path}` : 'from the repo'
  }
  // An unrecognised form is shown rather than hidden — a source this build has never
  // seen is still evidence of SOMETHING, and hiding it would look like no source at all.
  return source
}

/** "Round N of 2" — D3's two-round cap, stated so an operator knows how much runway is
 *  left before scoping stops asking and offers narrow-or-stop instead. */
export function scopingRoundLabel(round?: number): string {
  return `Round ${round && round > 0 ? round : 1} of 2`
}

/**
 * "Confirm this brief" is enabled only when no open question is left blank (build
 * contract, step 5). Every question's box is pre-filled with its default (D3: "most
 * answers are one click"), so this only ever disables the button for a question that
 * had NO default and whose box the operator also emptied out.
 */
export function scopingCanConfirm(open: OpenItem[], values: Record<string, string>): boolean {
  return open.every(o => (values[o.id ?? ''] ?? '').trim() !== '')
}

/**
 * The `answers` payload the decide route reads (build contract step 5): unchanged from
 * its pre-filled default means the operator accepted it, sent as `""` to match the
 * backend's contract exactly; anything the operator actually typed is sent as-is —
 * including a deliberately blanked box, which reads the same as "accept the default"
 * there being nothing else it could mean once no default existed.
 */
export function scopingAnswersPayload(
  open: OpenItem[], values: Record<string, string>,
): Record<string, string> {
  const answers: Record<string, string> = {}
  for (const o of open) {
    if (!o.id) continue
    const v = values[o.id] ?? ''
    answers[o.id] = v === (o.default ?? '') ? '' : v
  }
  return answers
}

export interface BriefSummaryLine {
  label: string
  value: string
  source: string
}

/**
 * The draft brief as a readable summary — build contract step 3's "kind, target file,
 * goal, trigger, inputs/returns, side effects with dry-run default, what stays
 * unchanged, where tests go / whether a new test file is allowed, acceptance, out of
 * scope". A pure function so the mapping from Brief to prose is tested without a DOM.
 */
export function briefSummaryLines(brief?: Brief | null): BriefSummaryLine[] {
  if (!brief) return []
  const src = brief.sources ?? {}
  const lines: BriefSummaryLine[] = []
  const push = (label: string, value: string, key: string) => {
    if (!value) return
    lines.push({ label, value, source: briefSourceLabel(src[key]) })
  }

  push('Kind', brief.kind === 'create' ? 'Create a new activity'
    : brief.kind === 'edit' ? 'Edit an existing activity' : brief.kind, 'kind')
  push('Target', brief.target?.name ? `${brief.target.file} (${brief.target.name})`
    : (brief.target?.file ?? ''), 'target.file')
  push('Goal', brief.goal, 'goal')
  if (brief.trigger?.kind) {
    push('Trigger', brief.trigger.detail ? `${brief.trigger.kind} — ${brief.trigger.detail}`
      : brief.trigger.kind, 'trigger.kind')
  }
  if (brief.inputs?.length) {
    push('Inputs', brief.inputs.map(i =>
      `${i.name}${i.type ? ` (${i.type})` : ''}${i.default ? ` = ${i.default}` : ''}`,
    ).join(', '), 'inputs')
  }
  if (brief.returns?.length) {
    push('Returns', brief.returns.map(r => `${r.name}${r.type ? ` (${r.type})` : ''}`).join(', '), 'returns')
  }
  if (brief.side_effects?.length) {
    push('Side effects', brief.side_effects.map(s =>
      `${s.tool}${s.what ? ` — ${s.what}` : ''}` +
      (s.dry_run_default === null || s.dry_run_default === undefined
        ? '' : ` (dry-run ${s.dry_run_default ? 'on' : 'off'} by default)`),
    ).join('; '), 'side_effects')
  }
  if (brief.unchanged?.length) {
    push('Stays unchanged', brief.unchanged.map(u =>
      u.whole_file ? `${u.file} (whole file)` : `${u.file}${u.tasks?.length ? `: ${u.tasks.join(', ')}` : ''}`,
    ).join('; '), 'unchanged')
  } else if (src['unchanged']) {
    // D2's second form of "nothing is ring-fenced" — an EXPLICIT empty, not a silent one.
    push('Stays unchanged', 'Nothing is ring-fenced', 'unchanged')
  }
  if (brief.task_changes?.length || src['task_changes']) {
    const byFile = new Map<string, { create: string[]; modify: string[] }>()
    for (const change of brief.task_changes ?? []) {
      const group = byFile.get(change.file) ?? { create: [], modify: [] }
      const operation = change.operation === 'create' ? 'create' : 'modify'
      group[operation].push(change.task)
      byFile.set(change.file, group)
    }
    const value = byFile.size === 0
      ? 'No task is named for change'
      : [...byFile.entries()].map(([file, group]) => {
          const parts: string[] = []
          if (group.create.length) parts.push(`create ${group.create.join(', ')}`)
          if (group.modify.length) parts.push(`modify ${group.modify.join(', ')}`)
          return `${parts.join(' · ')} (${file})`
        }).join('; ')
    push('Tasks to change', value, 'task_changes')
  }
  if (brief.tests) {
    const files = brief.tests.files?.length ? brief.tests.files.join(', ') : 'no test file named yet'
    const allowed = brief.tests.new_test_file_allowed === false ? 'a new test file is not allowed'
      : brief.tests.new_test_file_allowed === true ? 'a new test file is allowed' : ''
    push('Tests', allowed ? `${files} — ${allowed}` : files, 'tests')
  }
  if (brief.acceptance?.length) {
    push('Acceptance', brief.acceptance.join(' · '), 'acceptance')
  }
  if (brief.out_of_scope?.length) {
    push('Out of scope', brief.out_of_scope.join(' · '), 'out_of_scope')
  }
  return lines
}

/**
 * The message that drives the next phase.
 *
 * WORD FOR WORD WHAT THE authoring-e2e LANE SENDS. The lane has been advancing runs this
 * way since ADR-166 shipped; the operator UI never had the affordance at all, so a run
 * that finished a phase simply stopped, with the Process tab showing "Phase 2 of 7 — plan"
 * and no way to reach plan. ADR-167 D8 calls a state with no action a design bug, and this
 * was one — reported by the owner on 2026-09-02 as "Plan has passed but I see no activity
 * in design".
 *
 * Kept identical to the lane's string on purpose: two wordings would mean the surface an
 * operator drives and the surface CI measures are asking the model different questions,
 * and the lane's results would stop predicting the operator's.
 */
export function continuePhasePrompt(phase: Phase): string {
  return `Continue this authoring run. You are at phase ${phase}. ` +
    `Produce that phase's artifact and stop.`
}

/** 'ahead' | 'current' | 'behind' for the rail's rendering. */
export function phaseStanding(phase: Phase, current: Phase, collapse: boolean): 'ahead' | 'current' | 'behind' {
  const order = visiblePhases(collapse)
  const i = order.findIndex(p => samePhase(p, phase))
  const j = order.findIndex(p => samePhase(p, current))
  if (i < 0 || j < 0) return 'ahead'
  if (i === j) return 'current'
  return i < j ? 'behind' : 'ahead'
}

/**
 * The one-line summary of where a run is. THE STATE IS THE PRIMARY FIELD (ADR-167 D2).
 *
 * A pending checkpoint outranks the phase, because "phase 3 of 6" over a run that is
 * waiting on the operator describes the work and hides the only thing they can act on.
 */
export function runHeadline(run: AuthoringRunState | null, notConfigured: boolean,
  historyCount = 0, resumable = false): string {
  if (notConfigured) {
    return 'Phase state is not recorded on this Mission Control (no session store).'
  }
  // "YET" IS A CLAIM ABOUT HISTORY, AND THE HISTORY IS ON THE SAME PAGE.
  //
  // REPORTED BY THE OPERATOR 2026-09-17 with a screenshot: this line read "No authoring run
  // on this workflow yet." directly above the run they were watching —
  // 20260917T174002Z-1a0d63, "4 refused · still going", all seven phases listed, results
  // reached. Their words: "there is no place to select continue".
  //
  // They were right, and this sentence was wrong about why. The LIVE SLOT — one run per
  // (agent, workflow, operator) in the editor session — was empty, so the view route
  // returned `run: null` and every control went with it: the pacing control renders off the
  // live run, and AuthoringRedrivePhase answers 404 for an empty slot, so even "Ask for this
  // phase again…" in the history view cannot work.
  //
  // An operator reading "yet" concludes their run never started and goes looking for a Start
  // button. The truth is that the run happened, its RECORD survives, and its live state is
  // gone — a different situation with a different action. It is the same defect as the
  // Results card in this same session, one level up: there, absent measurement read as a
  // failed check; here, absent LIVE state reads as an absent RUN.
  //
  // AND "CANNOT BE RESUMED" STOPPED BEING TRUE HOURS AFTER IT WAS WRITTEN. The sentence
  // above shipped in PR #2538, which listed the gap under STILL OPEN; PR #2540 then built
  // the repair — POST /api/activities/authoring-run/resume and "Resume this run" on the
  // record-only card — and this line still told the operator it was impossible, directly
  // above the button that does it. Both render together: the run list is this rail's
  // `children`, and this headline is in the header that does not scroll.
  //
  // SO THE RAIL IS TOLD, NOT LEFT TO GUESS. Whether the control is on the page depends on
  // facts only the list holds — the newest recorded run is unfinished (the backend refuses
  // phase `done`) and the reader is editing — so `resumable` is the list's own gate,
  // reported upward the way the count already is.
  if (!run) {
    if (historyCount > 0) {
      const runs = `${historyCount} ${historyCount === 1 ? 'run is' : 'runs are'} in the ` +
        'record below'
      if (resumable) {
        return `No live authoring run on this workflow — ${runs}. The latest still has its ` +
          'whole record: use "Resume this run" on its card to put it back, or start a new run.'
      }
      return `No live authoring run on this workflow — ${runs} and cannot be resumed. ` +
        'Start a new run to continue.'
    }
    return 'No authoring run on this workflow yet.'
  }
  const pending = (run.checkpoints || []).find(c => !c.decision || c.decision === 'pending')
  if (pending) {
    const routed = (pending.open || []).filter(o => o.routed_for_review).length
    return `Waiting on you at ${pending.at_phase}` +
      (routed > 0 ? ` — ${routed} item${routed === 1 ? '' : 's'} routed for review` : '')
  }
  const order = visiblePhases(run.scope?.collapse_phases ?? false)
  const i = order.findIndex(p => samePhase(p, run.phase))
  const wherePos = `Phase ${i >= 0 ? i + 1 : '?'} of ${order.length}`
  const where = `${wherePos} — ${run.phase}`
  // A RUNNING turn reports the phase it is working on, never elapsed seconds. ADR-167 D2:
  // at 1016s "elapsed" cannot separate working from looping from stuck, and that sentence
  // is precisely what this replaces.
  if (run.turn_in_flight) {
    return `${where} — a phase is running now`
  }
  const last = (run.verdicts || [])[(run.verdicts || []).length - 1]
  const gate = !last ? 'no gate evaluated yet'
    : verdictClass(last) === 'pass' ? `${last.from} gate passed`
      : verdictClass(last) === 'wait' ? `${last.from} waited for a person; continuing`
        : `${last.from} gate REFUSED`
  // "AT a phase" AND "HAS DONE that phase" ARE DIFFERENT, and the rail said the same
  // sentence for both.
  //
  // A run is one turn per phase (ADR-166 D2), so passing the analysis gate leaves the run
  // sitting AT plan having produced no plan. "Phase 2 of 7 — plan" reads as though plan
  // ran, and the owner read it exactly that way on 2026-09-02: "Plan has passed but I see
  // no activity in design", then "actually I don't see the plan either".
  //
  // So a phase with no turn against it yet says it is WAITING, and says why nothing is
  // happening on its own.
  if (run.phase !== 'done' && !hasTurnAt(run, run.phase)) {
    // The POSITION IS KEPT. An earlier draft of this replaced the whole headline and lost
    // "2 of 7" — which a contract test caught, correctly: where the run is in the
    // sequence is the thing D7 added, and dropping it to fix the tense would trade one
    // missing fact for another.
    return `${wherePos} — waiting to run ${run.phase}; ${gate}. It has not produced a ${run.phase} yet.`
  }
  return `${where}; ${gate}`
}

/**
 * Has any turn actually run against this phase?
 *
 * Reads `turns`, not the phase pointer: the pointer says where the run STANDS, which is
 * one phase ahead of what it has DONE for the whole time it waits for the next turn.
 */
export function hasTurnAt(run: AuthoringRunState, phase: Phase): boolean {
  return (run.turns || []).some(t => samePhase(t.phase, phase))
}

function usableTime(value?: string): number | null {
  if (!value) return null
  const parsed = Date.parse(value)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : null
}

/** When the run's time in its CURRENT phase began, or null when nothing says. */
export function phaseStartedAt(run: AuthoringRunState | null): string | null {
  if (!run) return null

  let entry = Number.NEGATIVE_INFINITY
  for (const verdict of run.verdicts || []) {
    const movedIntoPhase = verdict.passed
      ? samePhase(verdict.to, run.phase)
      : samePhase(verdict.route_to, run.phase)
    if (!movedIntoPhase || samePhase(verdict.from, run.phase)) continue
    const decidedAt = usableTime(verdict.decided_at)
    if (decidedAt !== null) entry = Math.max(entry, decidedAt)
  }

  const candidates: number[] = []
  for (const turn of [...(run.turns || [])].reverse()) {
    if (!samePhase(turn.phase, run.phase)) break
    const startedAt = usableTime(turn.started_at)
    if (startedAt === null) continue
    if (startedAt < entry) break
    candidates.push(startedAt)
  }

  if (run.turn_in_flight) {
    const turnStartedAt = usableTime(run.turn_started_at)
    if (turnStartedAt !== null) candidates.push(Math.max(turnStartedAt, entry))
  }
  if (candidates.length === 0) return null
  return new Date(Math.min(...candidates)).toISOString()
}

export function phaseElapsed(run: AuthoringRunState | null, now: Date): { seconds: number; text: string } | null {
  if (!run?.turn_in_flight) return null
  const startedAt = phaseStartedAt(run)
  if (startedAt === null) return null
  const seconds = Math.max(0, Math.floor((now.getTime() - Date.parse(startedAt)) / 1000))
  return { seconds, text: `${Math.floor(seconds / 60)} min in ${run.phase}` }
}

/** "0.30" or "not rated" — the two must not look alike (the pointer distinction). */
export function confidenceLabel(o: OpenItem): string {
  return typeof o.confidence === 'number' ? o.confidence.toFixed(2) : 'not rated'
}

/**
 * Elapsed seconds, SECOND to the phase and never instead of it.
 *
 * ADR-167 D2 is that "working on it — 1016s elapsed" is not observability, because it
 * cannot separate working from looping from stuck. That argument is against elapsed as
 * THE state, not against showing it: next to "Running implementation" it is the thing
 * that tells an operator whether to keep waiting, which is exactly the decision they were
 * being asked to make with no information.
 */
function Elapsed({ since }: { since?: string }) {
  const [now, setNow] = useState(() => Date.now())
  useEffect(() => {
    const t = setInterval(() => setNow(Date.now()), 1000)
    return () => clearInterval(t)
  }, [])
  if (!since) return null
  const started = Date.parse(since)
  if (Number.isNaN(started)) return null
  const s = Math.max(0, Math.round((now - started) / 1000))
  return <> — {s < 60 ? `${s}s` : `${Math.floor(s / 60)}m ${s % 60}s`}</>
}

function Rail({ run }: { run: AuthoringRunState }) {
  const collapse = run.scope?.collapse_phases ?? false
  return (
    <div className="flex flex-wrap items-center gap-1 text-[10px]">
      {visiblePhases(collapse).map(p => {
        const s = phaseStanding(p, run.phase, collapse)
        const cls =
          s === 'current' ? 'bg-indigo-600 text-white font-semibold'
            : s === 'behind' ? 'bg-emerald-100 text-emerald-800'
              : 'bg-gray-100 text-gray-400'
        return <span key={p} className={`rounded px-1.5 py-0.5 ${cls}`}>{p}</span>
      })}
      {collapse && (
        <span className="text-gray-400" title="At node scope analysis, plan and design are one artifact (ADR-166 D1) — the run has one transition out of them, not three.">
          (node scope — three phases are one artifact)
        </span>
      )}
    </div>
  )
}

/**
 * ADR-200 D7 on a run with no brief: lift the request's own prohibition on the named files.
 *
 * A SEPARATE ACTION, NOT AN ANSWER. On spartan 20261017T145209Z-98ae93 the operator answered
 * "breach_check_test.py is in scope for exactly one change" and the rail went on removing the
 * edit, because an answer is evidence, not permission. The bound is required: it is what the
 * author and the reviewer hold the change to. The run returns to plan afterwards.
 */
function FenceLiftControl({
  paths, busy, onLift,
}: {
  paths: string[]
  busy: boolean
  onLift: (bounds: string) => void
}) {
  const [bounds, setBounds] = useState('')
  return (
    <div className="space-y-1 rounded border border-amber-200 bg-white px-2 py-1">
      <div className="text-[11px] font-medium text-gray-700">
        Your request says to leave {paths.length === 1 ? 'this file' : 'these files'} unchanged
      </div>
      <ul className="text-[11px] text-gray-800">
        {paths.map(p => <li key={p}><code>{p}</code></li>)}
      </ul>
      <input
        type="text"
        value={bounds}
        onChange={e => setBounds(e.target.value)}
        placeholder="What may change in it, e.g. line 767 only: 1.3.0 becomes 1.4.0"
        className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
      />
      <button
        type="button" disabled={busy || bounds.trim() === ''}
        onClick={() => onLift(bounds.trim())}
        className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
        title={bounds.trim() === ''
          ? 'Say what may change first: the change is held to that.'
          : 'Lift the prohibition for this run. The run returns to plan so the change is planned.'}
      >
        Lift prohibition
      </button>
    </div>
  )
}

/** Lift a measured published-test fence while keeping the run in its current phase and
 * releasing only the listed nodes. */
function PublishedTestsLiftControl({
  paths, removed, disabled, busy, onLift,
}: {
  paths: string[]
  removed: string[]
  disabled: string[]
  busy: boolean
  onLift: (bounds: string) => void
}) {
  const [bounds, setBounds] = useState('')
  return (
    <div className="space-y-1 rounded border border-amber-200 bg-white px-2 py-1">
      <div className="text-[11px] font-medium text-gray-700">
        This change removes or disables tests your fleet already has
      </div>
      <ul className="text-[11px] text-gray-600">
        {paths.map(path => <li key={`path:${path}`}><code>{path}</code></li>)}
      </ul>
      <ul className="text-[11px] text-gray-800">
        {removed.map(node => <li key={`removed:${node}`}><code>{node}</code> — removed</li>)}
        {disabled.map(node => <li key={`disabled:${node}`}><code>{node}</code> — disabled (skip or xfail)</li>)}
      </ul>
      <input
        type="text"
        value={bounds}
        onChange={e => setBounds(e.target.value)}
        placeholder="Why these tests may change, or how far, e.g. test_old_format is replaced by test_new_format"
        className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
      />
      <button
        type="button" disabled={busy || bounds.trim() === ''}
        onClick={() => onLift(bounds.trim())}
        className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
        title={bounds.trim() === ''
          ? 'Say why or how far first: the change is held to that.'
          : 'Allow only the listed tests to be removed or disabled, for this run. The run continues in its current phase.'}
      >
        Allow these test changes
      </button>
    </div>
  )
}

function TaskReplanControl({
  offer, busy, onReplan,
}: {
  offer: NonNullable<Checkpoint['task_replan']>
  busy: boolean
  onReplan: (reason: string, tasks: string[]) => void
}) {
  const [selected, setSelected] = useState<string[]>(() => [...offer.tasks])
  const [reason, setReason] = useState('')
  const toggle = (task: string) => setSelected(current => current.includes(task)
    ? current.filter(value => value !== task)
    : [...current, task])

  return (
    <div className="rounded border border-indigo-200 bg-white p-2 space-y-1.5">
      <div className="text-[11px] font-medium text-gray-800">Add the measured tasks to this run's scope</div>
      <div className="space-y-1">
        {offer.tasks.map(task => {
          const failure = offer.evidence?.find(item => item.task === task)?.failures?.[0]
          const test = failure?.test
          const testName = test?.split('::').pop() ?? ''
          return (
            <label key={task} className="flex items-start gap-1.5 text-[11px] text-gray-800">
              <input type="checkbox" checked={selected.includes(task)} onChange={() => toggle(task)} />
              <span>
                <span className="font-medium">{task}</span>
                {testName && <span className="block text-gray-500" title={test}>{testName}</span>}
              </span>
            </label>
          )
        })}
      </div>
      <input
        aria-label="Why should these tasks be added?"
        value={reason}
        onChange={event => setReason(event.target.value)}
        placeholder="Why add these tasks and replan?"
        className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
      />
      <button
        type="button"
        disabled={busy || selected.length === 0 || reason.trim() === ''}
        onClick={() => onReplan(reason.trim(), selected)}
        className="rounded bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
        title={reason.trim() === ''
          ? 'Give a reason and choose at least one task. This returns to plan.'
          : 'Add these tasks to the run and return to plan.'}
      >
        Add these tasks and replan
      </button>
      <div className="text-[10px] text-gray-500">Answer, Continue and Redirect do not add tasks.</div>
    </div>
  )
}

function CheckpointCard({
  cp, onDecide, busy, error,
}: {
  cp: Checkpoint
  /** `answer` is ADR-166 D6b's third payload, absent for continue/redirect/stop. Kept a
   *  separate argument rather than overloading `redirect`: the backend has had separate
   *  fields since D6b for the reason that an answer read as a redirect becomes an
   *  instruction instead of evidence. */
  onDecide: (decision: string, redirect: string, answer?: string, fenceLiftId?: string,
    taskReplan?: { id: string; tasks: string[] }) => void
  busy: boolean
  error: string
}) {
  const [redirect, setRedirect] = useState('')
  if (cp.omits && cp.omits.length > 0) {
    const files = cp.omits.join(', ')
    return (
      <div className="rounded border border-amber-300 bg-amber-50 p-2 space-y-2">
        <div className="text-xs text-amber-900">{cp.stuck_on}</div>
        <ul className="text-xs space-y-1">
          {(cp.open || []).map((item, i) => <li key={i}>{item.item}</li>)}
        </ul>
        <div className="flex flex-wrap gap-1">
          <button type="button" disabled={busy} onClick={() => onDecide('continue', '')}
            className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50">
            Ship without {files}
          </button>
          <button type="button" disabled={busy} onClick={() => onDecide('stop', '')}
            className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-red-300 disabled:opacity-50">
            Stop and wait for {files}
          </button>
        </div>
        {error && <div className="text-[11px] text-red-700">{error}</div>}
      </div>
    )
  }
  const resumes = hardTimeoutResumes(cp)
  const phase = String(cp.at_phase).replace(/_/g, ' ')
  const routed = (cp.open || []).filter(o => o.routed_for_review)
  const asksNothing = cp.reason === 'needs-information'
    && (cp.stuck_on || '').trim() === ''
    && (cp.open || []).length === 0
  const notReviewed = cp.reason === 'not-reviewed'
  const reReviewing = reReviewIsFresh(cp)
  const actionButtons = (
    <div className="flex flex-wrap gap-1">
      <button
        type="button" disabled={busy}
        onClick={() => onDecide('continue', '')}
        className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
        title={resumes
          ? `Retry ${phase} in a new job with a fresh time budget. The "did not complete" items are the phases still to run, not skipped review.`
          : asksNothing
          ? 'Continue. Nothing was asked, so there is nothing to answer first.'
          : routed.length > 0
            ? `Continue anyway. ${routed.length} item(s) stay marked as continued without review — the record keeps that.`
            : 'Continue from here.'}
      >
        {resumes ? 'Continue' : <>Continue{routed.length > 0 ? ` (over ${routed.length} unreviewed)` : ''}</>}
      </button>
      <button
        type="button" disabled={busy || redirect.trim() === ''}
        onClick={() => onDecide('redirect', redirect)}
        className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
        title={redirect.trim() === ''
          ? 'Type what to do differently first — an empty redirect is a continue and would return to the same place.'
          : 'Send it back with this instruction.'}
      >
        Redirect
      </button>
      <button
        type="button" disabled={busy}
        onClick={() => onDecide('stop', '')}
        className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-red-300 disabled:opacity-50"
        title={resumes
          ? 'Stop here. This ends the run: the artifacts stay readable, and a new build starts again from analysis.'
          : 'Stop here. The artifacts stay readable.'}
      >
        Stop
      </button>
    </div>
  )
  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-2 space-y-2">
      <div className="text-[11px] font-semibold text-amber-900">
        {reReviewing
          ? 'Re-reviewing this phase…'
          : asksNothing
          ? 'This run stopped to ask you something, but no question was recorded.'
          : checkpointHeadline(cp.reason, cp.stuck_on)}
        {typeof cp.elapsed_s === 'number' && cp.elapsed_s > 0 && <> ({cp.elapsed_s}s at {cp.at_phase})</>}
      </div>

      {asksNothing && (
        <div className="text-[11px] text-amber-900">
          The turn that paused here left no question and no open items, so there is nothing to answer. Continue moves the run on to its next turn; Stop ends it here.
        </div>
      )}

      {/* These placeholders are the timeout's own "did not complete" items; the operator in
          #3168 stopped a run that Continue would have resumed. Explain the retry explicitly. */}
      {resumes && (
        <div className="text-[11px] text-amber-900">
          Continue retries {phase} in a new job with a fresh time budget. Completed gates and saved changes are kept.
        </div>
      )}

      {cp.stuck_on && <div className="text-[11px] text-amber-900">{cp.stuck_on}</div>}

      {(cp.done || []).length > 0 && (
        <div className="text-[11px]">
          <span className="font-medium text-gray-700">Done:</span>{' '}
          <span className="text-gray-600">{(cp.done || []).join(' · ')}</span>
        </div>
      )}

      {(cp.open || []).length > 0 && (
        <div className="space-y-1">
          <div className="text-[11px] font-medium text-gray-700">Outstanding</div>
          {(cp.open || []).map((o, i) => (
            <div key={i} className={`rounded border px-2 py-1 text-[11px] ${o.routed_for_review ? 'border-amber-300 bg-white' : 'border-gray-200 bg-white'}`}>
              <div className="text-gray-800">{o.item}</div>
              <div className="text-gray-500">
                confidence {confidenceLabel(o)}
                {o.kind && <> · {o.kind}</>}
              </div>
              {/* THE QUESTION, not an approval request (D6). Rendering the route
                  without its question would send a reviewer somewhere with nothing
                  to answer. */}
              {o.routed_for_review && o.review_question && (
                <div className="mt-0.5 text-amber-900">→ {o.review_question}</div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* THE ANSWER, WHICH THIS CARD COULD NOT GIVE (ADR-181 D4 rule 6, ADR-166 D6b).
          `DecisionAnswer` and the decide route's `answer` field have existed since D6b,
          and NOTHING in this app ever sent one — the card offered continue, redirect and
          stop, so an operator answering a needs-information question had to type the fact
          into the Redirect box. That stores it in a field named for something else and the
          next turn reads it as a change of direction rather than as evidence, which is
          precisely what the Go comment on DecisionAnswer warns against. A consumer with no
          writer is the same defect as a writer with no consumer.

          Only for needs-information: a timebox checkpoint asks "continue, redirect or
          stop" and has no question an answer would be about. */}
      {/* The orion run persisted an empty needs-information checkpoint. Withholding the
          Answer box here avoids promising a question the record never supplied. */}
      {cp.fence_lift && cp.fence_lift.paths.length > 0 && (
        // KEYED BY THE OFFER, so a bound typed on one card never carries to the next.
        cp.fence_lift.kind === 'published_tests'
          ? <PublishedTestsLiftControl key={cp.fence_lift.id}
              paths={cp.fence_lift.paths}
              removed={cp.fence_lift.removed_tests ?? []}
              disabled={cp.fence_lift.disabled_tests ?? []}
              busy={busy}
              onLift={bounds => onDecide('lift_fence', '', bounds, cp.fence_lift?.id)} />
          : <FenceLiftControl key={cp.fence_lift.id} paths={cp.fence_lift.paths} busy={busy}
              onLift={bounds => onDecide('lift_fence', '', bounds, cp.fence_lift?.id)} />
      )}

      {cp.task_replan && cp.task_replan.tasks.length > 0 && (
        <TaskReplanControl key={cp.task_replan.id} offer={cp.task_replan} busy={busy}
          onReplan={(reason, tasks) => onDecide('add_tasks_replan', '', reason, undefined,
            { id: cp.task_replan!.id, tasks })} />
      )}

      {cp.reason === 'needs-information' && !asksNothing && (
        <AnswerControl
          items={(cp.open || []).filter(o => o.routed_for_review)}
          busy={busy}
          onAnswer={a => onDecide('answer', '', a)}
        />
      )}

      {notReviewed ? (
        <div className="flex flex-wrap gap-1">
          {!reReviewing && (
            <button
              type="button"
              disabled={busy}
              onClick={() => onDecide('continue', '')}
              className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
            >
              Confirm and re-review
            </button>
          )}
          <button
            type="button"
            disabled={busy}
            onClick={() => onDecide('stop', '')}
            className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-red-300 disabled:opacity-50"
            title="Stop here. The artifacts stay readable."
          >
            Stop
          </button>
        </div>
      ) : (
      /* EVERY STATE OFFERS A MOVE (ADR-167 D8). Three, because D6 names three. */
      <div className="space-y-1">
        {asksNothing && actionButtons}
        <input
          type="text"
          value={redirect}
          onChange={e => setRedirect(e.target.value)}
          placeholder="Redirect: say what to do differently"
          className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
        />
        {!asksNothing && actionButtons}
        {error && <div className="text-[11px] text-red-700">{error}</div>}
      </div>
      )}
      {notReviewed && error && <div className="text-[11px] text-red-700">{error}</div>}
    </div>
  )
}

/**
 * ADR-200 stage 1 — the scoping checkpoint's card, rendered instead of CheckpointCard
 * when `cp.reason === 'scoping'`.
 *
 * THREE SHAPES, one component, because the build contract routes to three outcomes from
 * the same gate:
 *  - the round-cap (`cp.stuck_on` says scoping did not converge in two rounds) — narrow
 *    or stop, no question controls;
 *  - the envelope's split/decline verdict — accept the verdict or stop, no question
 *    controls;
 *  - the ordinary round — the draft brief, one control per open question, and
 *    "Answer and continue" / "Confirm this brief".
 *
 * NO GATE CODES, NO CLAIM IDS (D3, ADR-181 D4 rule 1) — every string here is prose an
 * operator who has never heard of `analysis.brief_unconfirmed` can act on.
 */
export function ScopingCard({
  cp, run, onDecideBrief, onKeepBrief, onDecide, busy, error,
}: {
  cp: Checkpoint
  run: AuthoringRunState
  /** The new decision (build contract step 5): `answers` keyed by question id, `confirm`
   *  true only once nothing is left open. Separate from `onDecide` because a scoping
   *  round answers SEVERAL questions at once — `onDecide`'s single `answer` field is
   *  ADR-181 D6b's one-question shape and does not fit. */
  onDecideBrief: (answers: Record<string, string>, confirm: boolean) => void
  onKeepBrief?: () => void
  onDecide: (decision: string, redirect: string, answer?: string) => void
  busy: boolean
  error: string
}) {
  const [redirect, setRedirect] = useState('')
  const open = cp.open ?? []
  // PRE-FILLED WITH THE DEFAULT (D3), and initialised once per round: the caller keys
  // this component on `brief_round` so a new round's questions get a fresh box rather
  // than carrying over an old round's edits under a reused id.
  const [values, setValues] = useState<Record<string, string>>(() => {
    const v: Record<string, string> = {}
    for (const o of open) if (o.id) v[o.id] = o.default ?? ''
    return v
  })
  const setValue = (id: string, v: string) => setValues(prev => ({ ...prev, [id]: v }))

  const roundCap = (cp.stuck_on ?? '').toLowerCase().includes('did not converge')
  const envelope = run.brief?.envelope
  const envelopeOut = !roundCap && !!envelope
    && (envelope.verdict === 'split' || envelope.verdict === 'decline')

  if (roundCap) {
    return (
      <div className="rounded border border-amber-300 bg-amber-50 p-2 space-y-2">
        <div className="text-[11px] font-semibold text-amber-900">
          This request needs to be narrowed before I can agree a brief.
        </div>
        <div className="text-[11px] text-amber-900">{cp.stuck_on}</div>
        <div className="space-y-1">
          <input
            type="text"
            value={redirect}
            onChange={e => setRedirect(e.target.value)}
            placeholder="Narrow the request: say what to leave out"
            className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
          />
          <div className="flex flex-wrap gap-1">
            <button
              type="button" disabled={busy || redirect.trim() === ''}
              onClick={() => onDecide('redirect', redirect)}
              className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
              title={redirect.trim() === ''
                ? 'Say what to narrow first — an empty redirect returns to the same ambiguity.'
                : 'Send the narrowed request back to scoping.'}
            >
              Narrow the request
            </button>
            <button
              type="button" disabled={busy}
              onClick={() => onDecide('stop', '')}
              className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-red-300 disabled:opacity-50"
              title="Stop here. The draft brief and the questions stay on the record."
            >
              Stop the run
            </button>
          </div>
          {error && <div className="text-[11px] text-red-700">{error}</div>}
        </div>
      </div>
    )
  }

  if (envelopeOut && envelope) {
    const isSplit = envelope.verdict === 'split'
    return (
      <div className="rounded border border-amber-300 bg-amber-50 p-2 space-y-2">
        <div className="text-[11px] font-semibold text-amber-900">
          {isSplit
            ? 'This is bigger than one run can build reliably — it splits into several.'
            : 'This is outside what the rail can build reliably yet, so it declines rather than attempt it.'}
        </div>
        {envelope.reason && <div className="text-[11px] text-amber-900">{envelope.reason}</div>}
        {isSplit && (envelope.split ?? []).length > 0 && (
          <div className="space-y-1">
            <div className="text-[11px] font-medium text-gray-700">
              Proposed split — each becomes its own run, in order
            </div>
            <ol className="list-decimal pl-4 text-[11px] text-gray-800 space-y-0.5">
              {(envelope.split ?? []).map((s, i) => <li key={i}>{s}</li>)}
            </ol>
          </div>
        )}
        <div className="flex flex-wrap gap-1">
          <button
            type="button" disabled={busy}
            onClick={() => onDecideBrief({}, true)}
            className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
            title={isSplit
              ? 'Accept the split. This run ends with the split briefs listed; each is authored separately.'
              : 'Accept the decline. This run ends here.'}
          >
            Accept
          </button>
          <button
            type="button" disabled={busy}
            onClick={() => onDecide('stop', '')}
            className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-red-300 disabled:opacity-50"
          >
            Stop the run
          </button>
        </div>
        {error && <div className="text-[11px] text-red-700">{error}</div>}
      </div>
    )
  }

  const summary = briefSummaryLines(run.brief)
  const canConfirm = scopingCanConfirm(open, values)
  // ADR-200 D7 — AN AMENDMENT SAYS SO. The operator already agreed a brief; what they are
  // being asked now is to correct it, and the card must not read like a first scoping.
  const amendment = amendmentFor(cp, run)

  return (
    <div className="rounded border border-amber-300 bg-amber-50 p-2 space-y-2">
      <div className="text-[11px] font-semibold text-amber-900">
        {amendment
          ? `The agreed brief needs amending — brief@${amendment.from_version} becomes brief@${amendment.from_version + 1}`
          : <>Before I build this, let&apos;s agree what it is</>}
        <span className="ml-1 font-normal text-amber-800">— {scopingRoundLabel(run.brief_round)}</span>
      </div>
      {amendment && (
        <div className="rounded border border-amber-200 bg-white px-2 py-1.5 text-[11px] text-amber-900"
          data-testid="brief-amendment">
          The {String(amendment.raised_at_phase).replace(/_/g, ' ')} phase found the brief wrong
          about <span className="font-medium">{amendment.field}</span>
          {amendment.problem ? <>: {amendment.problem}</> : '.'} Once you confirm the amended
          brief, the run resumes from the earliest phase the change affects.
        </div>
      )}

      {summary.length > 0 && (
        <div className="space-y-1 rounded border border-amber-200 bg-white px-2 py-1.5">
          <div className="text-[11px] font-medium text-gray-700">The draft brief</div>
          {summary.map(l => (
            <div key={l.label} className="text-[11px]">
              <span className="font-medium text-gray-700">{l.label}:</span>{' '}
              <span className="text-gray-800">{l.value}</span>
              {l.source && <span className="text-gray-400"> ({l.source})</span>}
            </div>
          ))}
        </div>
      )}

      {open.length > 0 && (
        <div className="space-y-1">
          <div className="text-[11px] font-medium text-gray-700">Open questions</div>
          {open.map((o, i) => (
            <div key={o.id ?? i} className="space-y-0.5 rounded border border-gray-200 bg-white px-2 py-1">
              <div className="text-[11px] text-gray-800">{o.item}</div>
              {o.review_question && o.review_question !== o.item && (
                <div className="text-[11px] text-gray-500">{o.review_question}</div>
              )}
              {o.id && (
                o.options && o.options.length > 0 ? (
                  <select
                    value={values[o.id] ?? ''}
                    onChange={e => setValue(o.id as string, e.target.value)}
                    className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
                  >
                    {o.options.map(opt => <option key={opt} value={opt}>{opt}</option>)}
                  </select>
                ) : (
                  <input
                    type="text"
                    value={values[o.id] ?? ''}
                    onChange={e => setValue(o.id as string, e.target.value)}
                    className="w-full rounded border border-gray-300 px-2 py-1 text-[11px]"
                  />
                )
              )}
              {o.default && (
                <div className="text-[11px] text-gray-400">
                  Prefilled with the proposed default — leave it as-is to accept it.
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      <div className="space-y-1">
        <div className="flex flex-wrap gap-1">
          {open.length > 0 && (
            <button
              type="button" disabled={busy}
              onClick={() => onDecideBrief(scopingAnswersPayload(open, values), false)}
              className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
              title="Send these answers. If any question is still open, scoping asks again — up to one more round."
            >
              Answer and continue
            </button>
          )}
          <button
            type="button" disabled={busy || !canConfirm}
            onClick={() => onDecideBrief(scopingAnswersPayload(open, values), true)}
            className="rounded bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50"
            title={canConfirm
              ? 'Agree this brief. Analysis and every phase after it work from exactly this.'
              : 'Answer every open question first — an unanswered one has no field to confirm.'}
          >
            {amendment ? 'Confirm the amended brief' : 'Confirm this brief'}
          </button>
          {amendment && onKeepBrief && (
            <button
              type="button" disabled={busy}
              onClick={onKeepBrief}
              className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-indigo-300 disabled:opacity-50"
              data-testid="brief-keep-unchanged"
              title={`Keep brief@${amendment.from_version} exactly as agreed; the ${String(amendment.raised_at_phase).replace(/_/g, ' ')} phase re-runs under the same gates with no new retry budget`}
            >
              Keep this brief unchanged
            </button>
          )}
          <button
            type="button" disabled={busy}
            onClick={() => onDecide('stop', '')}
            className="rounded bg-white border border-gray-300 px-2 py-0.5 text-[11px] hover:border-red-300 disabled:opacity-50"
          >
            Stop the run
          </button>
        </div>
        {error && <div className="text-[11px] text-red-700">{error}</div>}
      </div>
    </div>
  )
}

/**
 * ADR-200 D1/D2 — the agreed brief, read-only and collapsed by default, so a reviewer
 * sees WHAT WAS AGREED without the run rail growing a permanent block of prose.
 *
 * Rendered even when `canEdit` is false — unlike the rest of this file's controls, this
 * is not a write, and "reviewers see what was agreed" (D2) is exactly the case of
 * someone reading this rail without the operator's own edit rights.
 */
function ConfirmedBriefSummary({
  brief, confirmedAt, confirmedBy, versions, amendments, onStore,
}: {
  brief?: Brief
  confirmedAt: string
  confirmedBy?: string
  /** ADR-200 D7 — every confirmed version, so a reader sees what was agreed AND when it changed. */
  versions?: BriefVersion[]
  amendments?: BriefAmendment[]
  /** ADR-200 D6 — save this brief so an unattended start can reference it. Absent when the
   *  reader cannot write; the brief itself stays readable either way. */
  onStore?: () => Promise<string>
}) {
  const summary = briefSummaryLines(brief)
  const [stored, setStored] = useState('')
  const [storeErr, setStoreErr] = useState('')
  const [storing, setStoring] = useState(false)
  const store = async () => {
    if (!onStore) return
    setStoring(true)
    setStoreErr('')
    try {
      setStored(await onStore())
    } catch (e) {
      setStoreErr(e instanceof Error ? e.message : 'the brief could not be stored')
    } finally {
      setStoring(false)
    }
  }
  const latest = versions && versions.length > 0 ? versions[versions.length - 1] : undefined
  return (
    <details className="rounded border border-emerald-200 bg-emerald-50 px-2 py-1 text-[11px] text-emerald-900">
      <summary className="cursor-pointer font-medium">
        Agreed brief{latest ? ` (brief@${latest.version})` : ''}
        {confirmedBy ? ` · confirmed by ${confirmedBy}` : ' · confirmed'}
      </summary>
      <div className="mt-1 space-y-1">
        <div className="text-emerald-800">Confirmed {confirmedAt}</div>
        {summary.map(l => (
          <div key={l.label}>
            <span className="font-medium">{l.label}:</span>{' '}
            <span>{l.value}</span>
            {l.source && <span className="text-emerald-700"> ({l.source})</span>}
          </div>
        ))}
        {versions && versions.length > 1 && (
          <div className="space-y-0.5 pt-1" data-testid="brief-versions">
            <div className="font-medium">How the brief changed</div>
            <ol className="list-decimal pl-4">
              {versions.map(v => (
                <li key={v.version}>{briefVersionLine(v)} — {v.confirmed_at}</li>
              ))}
            </ol>
          </div>
        )}
        {amendments && amendments.length > 0 && (
          <div className="space-y-0.5 pt-1" data-testid="brief-amendments">
            {amendments.filter(amendment => amendment.resolved_at).map(amendment => (
              <div key={amendment.id}>
                {String(amendment.raised_at_phase).replace(/_/g, ' ')} phase reported about {amendment.field}
                {amendment.problem ? `: ${amendment.problem}` : ''}
                {amendment.outcome === 'reconfirmed'
                  ? ' — kept unchanged (no contract changes)'
                  : amendment.outcome === 'amended' ? ' — amended' : ''}
              </div>
            ))}
          </div>
        )}
        {onStore && (
          <div className="pt-1">
            {stored
              ? <div className="text-emerald-800">
                  Stored. An unattended start (a trigger, a lane dispatch) that sends{' '}
                  <code className="font-mono">brief_ref: {stored}</code> skips scoping and
                  works from this brief.
                </div>
              : <button type="button" disabled={storing} onClick={() => void store()}
                  className="rounded border border-emerald-300 bg-white px-2 py-0.5 hover:bg-emerald-100 disabled:opacity-50"
                  title="Save this agreed brief so a run started without a person can use it instead of waiting for one.">
                  Save for unattended runs
                </button>}
            {storeErr && <div className="text-red-700">{storeErr}</div>}
          </div>
        )}
      </div>
    </details>
  )
}

/** True only when the server's own error names a pull request it opened but could not
 * confirm — never on text quoted inside the error, such as a failing test's output. */
function proposalOpenedButUnconfirmed(message: string): boolean {
  const prefix = 'HTTP 502: '
  if (!message.startsWith(prefix)) return false
  try {
    const body = JSON.parse(message.slice(prefix.length))
    return typeof body.error === 'string' && /^the pull request was opened \([^)]+\) but /.test(body.error)
  } catch {
    return false
  }
}

export function AuthoringRail({
  agent, workflow, onContinue, continuing, canEdit, children, historyCount = 0,
  onLiveRun, resumable = false, onProposed,
}: {
  agent: string
  workflow: string
  /** Drives the next phase through the SAME send a typed turn uses. Optional so the
   *  component still renders in isolation; absent means the button is not offered rather
   *  than offered and dead. */
  onContinue?: (prompt: string) => void
  continuing?: boolean
  /** Whether the operator is editing. Gates the controls that WRITE — driving a phase,
   *  answering a checkpoint, discarding the run. The state in the header above them and
   *  whatever is nested as `children` below are readable either way. */
  canEdit?: boolean
  /** How many runs the RECORD holds, supplied by the caller that renders the list.
   *
   *  The rail cannot count them itself — the list is `children` — and without the count its
   *  headline claimed no run had ever started while five sat below it. See runHeadline. */
  historyCount?: number
  /** Whether the run list is offering "Resume this run" on the newest recorded run.
   *
   *  THE HEADLINE MUST NOT CONTRADICT THE CARD BELOW IT. It used to state that recorded runs
   *  "cannot be resumed" — true when it was written, and false a few hours later once the
   *  resume route landed. The gate is the list's (an unfinished newest run, in edit mode),
   *  so the list reports it rather than this component inferring it. See runHeadline. */
  resumable?: boolean
  /** Called after a successful proposal with the slot that the operator clicked. */
  onProposed?: (agent: string, workflow: string) => void
  /** The id of the run in the LIVE SLOT, reported upward — or null when the slot is empty.
   *
   *  THE RUN LIST CANNOT WORK THIS OUT. It reads the RECORD, where the newest row is not
   *  necessarily the run in the slot, and the difference decides which controls can act: a
   *  redrive against a record-only run answers 404. This component is the one that fetches
   *  the live view, so it is the one that knows. */
  onLiveRun?: (runId: string | null) => void
  /** The run history, rendered inside the rail's SCROLLING half.
   *
   *  A child rather than a sibling so the rail can be a flex column whose header does not
   *  scroll. As siblings in one scroller the header had to be `sticky`, and sticky cannot
   *  leave its own parent's box — it pinned while the rail's div was on screen and then
   *  scrolled away with it, which is the defect this shape removes rather than tunes. */
  children?: React.ReactNode
}) {
  const [run, setRun] = useState<AuthoringRunState | null>(null)
  const [notConfigured, setNotConfigured] = useState(false)
  const [err, setErr] = useState('')
  const [busy, setBusy] = useState(false)
  const [decideErr, setDecideErr] = useState('')
  const notifyAttention = useAttentionNotify()
  const runRef = useRef(run)
  const continuingRef = useRef(!!continuing)
  const checkpointSeenRef = useRef<string | null | undefined>(undefined)

  const load = useCallback(async () => {
    if (!agent || !workflow) return
    try {
      const r = await api.authoringRun(agent, workflow)
      const nextRun = r.run ?? null
      const attentionId = checkpointAttentionId(nextRun)
      if (checkpointSeenRef.current === undefined) {
        checkpointSeenRef.current = attentionId
      } else {
        if (attentionId && attentionId !== checkpointSeenRef.current) {
          notifyAttention({ id: attentionId, source: 'authoring-checkpoint', reason: 'input-needed' })
        }
        checkpointSeenRef.current = attentionId
      }
      setRun(nextRun)
      setNotConfigured(!!r.not_configured)
      setErr('')
    } catch (e) {
      // NOT "no run". The read failed, and saying "no authoring run" here would be the
      // same lie as rendering an unreadable check as a passing one.
      setErr(e instanceof Error ? e.message : 'could not read the authoring run')
    }
  }, [agent, workflow, notifyAttention])

  useEffect(() => { checkpointSeenRef.current = undefined }, [load])
  useEffect(() => { void load() }, [load])
  useEffect(() => { runRef.current = run }, [run])
  useEffect(() => {
    const wasContinuing = continuingRef.current
    continuingRef.current = !!continuing
    if (wasContinuing && !continuing) void load()
  }, [continuing, load])
  useEffect(() => {
    const t = setInterval(() => {
      if (!document.hidden || railPollsWhileHidden(runRef.current, new Date(), continuingRef.current)) void load()
    }, 5000)
    return () => clearInterval(t)
  }, [load])

  // WHICH RUN IS IN THE SLOT, reported upward.
  //
  // AN EFFECT ON `run`, NOT A CALL AT EACH setRun. There are three of those — the poll, the
  // reset and the checkpoint decision — and a report added to two of them is a list that
  // goes stale silently: after a reset the run list would go on offering redrive against a
  // slot that had just been emptied. One effect covers every path that can change it,
  // including the one that sets it to null.
  useEffect(() => { onLiveRun?.(run?.run_id ?? null) }, [run, onLiveRun])

  const stopped = runStoppedBy(run)
  const pending = stopped ? undefined : (run?.checkpoints || []).find(c => !c.decision || c.decision === 'pending')

  const [confirmReset, setConfirmReset] = useState(false)
  // CANCEL HAS ITS OWN CONFIRM AND ITS OWN ERROR, for the reason resetErr documents: one
  // shared slot meant an error from one action appeared under the other.
  const [confirmCancel, setConfirmCancel] = useState(false)
  const [cancelErr, setCancelErr] = useState('')
  // PROPOSE HAS ITS OWN ERROR, for the reason cancel and reset do: one shared slot meant an
  // error from one action appeared under another.
  // WHAT THE PROPOSAL IS NOW. Approved and deployed in another tab is the normal case, and
  // this surface has to notice or it keeps offering an approval for work that is live.
  const proposalStatus = useImprovementStatus(run?.proposal?.improvement_id, run?.proposal?.url)
  const [proposeErr, setProposeErr] = useState('')
  const [proposing, setProposing] = useState(false)
  // A RUN ANY STOP DECIDED TAKES NO MORE TURNS. Read from the checkpoint like AuthoringRun.stoppedAt() on the backend, never from a reason code: Cancel run's operator-cancelled record is one stop among several.
  // ITS OWN ERROR STATE, not the checkpoint's. Sharing one slot meant a failed reset
  // rendered inside the CheckpointCard when a checkpoint happened to be pending, and
  // nowhere at all when one was not — so the button reported nothing in exactly the
  // stuck case it exists for.
  const [resetErr, setResetErr] = useState('')

  // ADR-167 D8 — the way back to empty.
  //
  // Two-step on purpose. Discarding a run throws away every gate verdict and cited claim
  // it accumulated, and those are the record of how a change was arrived at; a one-click
  // destroy next to a "Continue" button is a misclick away from losing them.
  const reset = async () => {
    setBusy(true)
    setResetErr('')
    try {
      await api.resetAuthoringRun(agent, workflow)
      setRun(null)
      setConfirmReset(false)
    } catch (e) {
      setResetErr(e instanceof Error ? e.message : 'the run could not be cleared')
    } finally {
      setBusy(false)
    }
  }

  // STOP THIS RUN, WHATEVER IT IS DOING.
  //
  // Offered in every state INCLUDING mid-turn, which is the difference from reset next to
  // it. Reset is disabled while a turn runs because discarding the phase state does not
  // stop the turn; cancel marks the run stopped and Mission Control discards the turn's
  // result when it lands, so the work cannot come back. Measured need: a turn sat
  // `in_flight` for 75 minutes past a 41-minute budget after a container restart, with no
  // checkpoint and nothing to press.
  //
  // Two-step like reset, but the confirm says something different: nothing is destroyed
  // here, so the question is about the work in progress rather than the record.
  const cancelRun = async () => {
    setBusy(true)
    setCancelErr('')
    try {
      await api.cancelAuthoringRun(agent, workflow)
      setConfirmCancel(false)
      await load()
    } catch (e) {
      setCancelErr(e instanceof Error ? e.message : 'the run could not be cancelled')
    } finally {
      setBusy(false)
    }
  }

  // ADR-178 D5 — pause, resume, next.
  //
  // ITS OWN ERROR STATE, for the reason resetErr documents above: sharing one slot meant a
  // failed action reported in the wrong place, or nowhere.
  const [pacingErr, setPacingErr] = useState('')

  // A ONE-SECOND TICK, ONLY TO RE-RENDER.
  //
  // The deadline is the SERVER's (D1) and this clock does not own it — autoAdvanceSecondsLeft
  // is pure and derives the number from the run's own `next_turn_at`. If this interval never
  // ran, the countdown would simply stop updating; the turn would still fire, because
  // Mission Control is the thing counting. That is the whole point of D1.
  const [now, setNow] = useState(() => new Date())
  useEffect(() => {
    const t = setInterval(() => setNow(new Date()), 1000)
    return () => clearInterval(t)
  }, [])

  const pace = async (action: 'pause' | 'resume' | 'next') => {
    setBusy(true)
    setPacingErr('')
    try {
      await api.autoAdvance(agent, workflow, action)
      // RE-READ RATHER THAN GUESS. The server decides whether the timer re-armed at all —
      // a resume on a run with a pending checkpoint correctly arms nothing — so patching
      // local state from the action would show a countdown the backend does not have.
      await load()
    } catch (e) {
      setPacingErr(e instanceof Error ? e.message : 'the control could not be applied')
    } finally {
      setBusy(false)
    }
  }

  const decide = async (decision: string, redirect: string, answer?: string, fenceLiftId?: string,
    taskReplan?: { id: string; tasks: string[] }) => {
    setBusy(true)
    setDecideErr('')
    try {
      const r = taskReplan
        ? await api.decideAuthoringCheckpoint(agent, workflow, decision, redirect, answer, undefined, fenceLiftId, taskReplan)
        : fenceLiftId
        ? await api.decideAuthoringCheckpoint(agent, workflow, decision, redirect, answer, undefined, fenceLiftId)
        : await api.decideAuthoringCheckpoint(agent, workflow, decision, redirect, answer)
      setRun(r.run ?? null)
    } catch (e) {
      setDecideErr(e instanceof Error ? e.message : 'the decision could not be recorded')
    } finally {
      setBusy(false)
    }
  }

  // ADR-200 stage 1 — the "brief" decision. A SEPARATE call from `decide` above rather
  // than another positional argument on it: a scoping round answers several questions
  // at once (`answers`), and `decide`'s single `answer` field is ADR-181 D6b's
  // one-question shape.
  const decideBrief = async (answers: Record<string, string>, confirm: boolean) => {
    setBusy(true)
    setDecideErr('')
    try {
      const r = await api.decideAuthoringBrief(agent, workflow, answers, confirm)
      setRun(r.run ?? null)
    } catch (e) {
      setDecideErr(e instanceof Error ? e.message : 'the decision could not be recorded')
    } finally {
      setBusy(false)
    }
  }

  const keepBrief = async () => {
    setBusy(true)
    setDecideErr('')
    try {
      const r = await api.reconfirmAuthoringBrief(agent, workflow)
      setRun(r.run ?? null)
    } catch (e) {
      setDecideErr(e instanceof Error ? e.message : 'the brief could not be reconfirmed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div className="flex h-full min-h-0 flex-col">
      {err && (
        <div className="rounded border border-red-300 bg-red-50 p-2 text-[11px] text-red-800">
          <strong>Could not read this run&apos;s phase state.</strong> {err}
          <button type="button" onClick={() => void load()}
            className="ml-2 rounded border border-red-300 bg-white px-1.5 py-0.5 hover:bg-red-100">
            Retry
          </button>
        </div>
      )}

      {/* THE HEADER, OUTSIDE THE SCROLLING BOX (ADR-177).
          Where the run IS must not scroll away from the artifacts it describes: the tree
          is several screens once a run has seven phases of claims and diffs in it, and
          reading phase four while the phase rail sits two screens up is how an operator
          loses their place.

          `shrink-0` in a flex column, NOT `sticky`. Sticky was the first attempt and it
          is the wrong tool: it cannot leave its own parent's box, so the header pinned
          while the rail's div was on screen and then scrolled away with it — "it stays if
          you scroll some but not for total surface vertical scroll". There is no scroll
          position at which this can be off screen, because it is not in the scrolling
          box. */}
      <div className="shrink-0 space-y-2 border-b border-gray-200 bg-white px-2 py-2">
        <div className="text-[11px] text-gray-700">{runHeadline(run, notConfigured, historyCount, resumable)}</div>
        {run && (
          <>
            <div className="text-[10px] text-gray-500">
              scope <span className="font-medium text-gray-700">{run.scope?.scope}</span>
              {run.scope?.reason && <> — {run.scope.reason}</>}
            </div>
            <Rail run={run} />
          {/* A RUNNING TURN HAS TO LOOK LIKE ONE.
                The headline said "a phase is running now" in the same grey prose as
                everything else, and the only BUTTON on screen was "Start a new run" —
                which discards the run. Reported 2026-09-02: "it is not clear it is working
                or in progress; temptation is to pick Start a new run instead of being
                patient." An interface whose most visible control is the destructive one,
                during the wait it is asking you to sit through, is inviting the mistake. */}
            {run.turn_in_flight && (
              <div className="flex items-center gap-2 rounded border border-indigo-300 bg-indigo-50 p-2">
                <span className="inline-block h-2 w-2 shrink-0 animate-pulse rounded-full bg-indigo-600" />
                <span className="text-[11px] text-indigo-900">
                  <span className="font-semibold">Running {run.phase}</span>
                  <Elapsed since={phaseStartedAt(run) ?? run.turn_started_at} />
                  {' in this phase — each turn is a model call and takes a few minutes. Nothing is stuck.'}
                </span>
              </div>
            )}
          </>
        )}
      </div>

      {/* THE SCROLLING HALF. Everything below the header lives here, including whatever
          the caller nested as `children` — the run history — so the header above stays
          put however far down the tree an operator reads. */}
      <div className="flex-1 min-h-0 space-y-2 overflow-auto p-2">
      {/* ADR-200 D1/D2 — THE AGREED BRIEF, read-only, visible whether or not this reader
          can edit. "The gates read the brief, not the prose" is a claim a reviewer has
          to be able to check, and gating this on canEdit would hide it from exactly the
          reader most likely to be reviewing rather than driving the run. */}
      {run?.brief_confirmed_at && (
        <ConfirmedBriefSummary
          brief={run.brief} confirmedAt={run.brief_confirmed_at} confirmedBy={run.brief_confirmed_by}
          versions={run.brief_versions}
          amendments={run.brief_amendments}
          onStore={canEdit && run.brief?.envelope?.verdict === 'in'
            ? async () => (await api.storeAuthoringBrief(agent, workflow)).stored_brief.id
            : undefined}
        />
      )}
      {run && canEdit && (
        <>

          {/* ADR-178 D1/D5 — THE PACING CONTROL.
              A state and a countdown, not a spinner (ADR-167). The seconds come from the
              server's own deadline; this surface renders them and asks Mission Control to
              change them, and owns no clock of its own — a browser that owned it would stop
              the rail silently when the tab closed. */}
          {(() => {
            const st = autoAdvanceStatus(run, now, proposalStatus)
            if (st.kind === 'finished' && !run.proposal && !stopped) return null
            const tone =
              st.kind === 'counting' ? 'border-blue-300 bg-blue-50 text-blue-900'
              : st.kind === 'paused' ? 'border-amber-300 bg-amber-50 text-amber-900'
              : st.kind === 'blocked' ? 'border-purple-300 bg-purple-50 text-purple-900'
              : st.kind === 'running' ? 'border-gray-300 bg-gray-50 text-gray-700'
              : run.proposal?.error ? 'border-red-300 bg-red-50 text-red-900'
              : run.proposal?.registration_state === 'pending' && !proposalStatus ? 'border-sky-300 bg-sky-50 text-sky-900'
              : run.proposal?.registration_state === 'blocked' || (run.proposal?.improvement_warning && !proposalStatus)
                ? 'border-amber-300 bg-amber-50 text-amber-900'
              : 'border-green-300 bg-green-50 text-green-900'
            return (
              <div className={`mt-2 flex flex-wrap items-center gap-2 rounded border px-2 py-1.5 text-[11px] ${tone}`}
                   data-testid="pacing">
                <span className="font-medium" data-testid="pacing-status">{st.text}</span>
                {/* ELAPSED AGAINST A BUDGET, WHICH IS NOT THE THING D2 REFUSED.
                    ADR-167 D2 rejected a bare elapsed counter, and rightly: "at 1016s
                    `elapsed` cannot separate working from looping from stuck". A number with
                    the LIMIT beside it can — 28 of 41 is patience, 64 of 41 is a problem —
                    and the phase note below says why a slow phase is slow. The headline
                    still leads with the phase, so nothing D2 asked for is lost.

                    An operator watching a real run on 2026-09-09 had none of this and said
                    so: "the UI gives absolutely no help in solving this or guiding the
                    operator." */}
                {(() => {
                  const phase = phaseElapsed(run, now)
                  const turn = turnElapsed(run, now)
                  if (!phase && !turn) return null
                  const pacing = phase && turn
                    ? `· ${phase.text} · ${turn.text}`
                    : `· ${(phase ?? turn)!.text}`
                  return (
                    <span data-testid="pacing-elapsed"
                          className={turn?.overBudget ? 'font-medium text-red-700' : 'opacity-80'}>
                      {pacing}
                    </span>
                  )
                })()}
                {/* INTO MISSION CONTROL, NEVER OUT TO GITHUB.
                    This rendered <a href={proposal.url} target="_blank">open the pull
                    request</a>, sending the operator to github.com — while every other
                    propose surface (ProposeResultBanner, DeletePanel, SchemaBuilder,
                    RoutingFacet) links to /improvements/:id/pr. The backend says why:
                    "approvals never require GitHub (ADR-079) ... URL is retained for
                    audit/logging but is NOT surfaced as a GitHub link in the UI."
                    A run that reaches `done` and the Propose BUTTON must land in the same
                    place; approving on GitHub is a different act on a different surface. */}
                {/* THE WARNING REPLACES THE HANDOFF, and carries no GitHub link with it.
                    Registration failed, so neither of the two things below is true: there is
                    no in-app review handle to deep-link, and the Improvements list does not
                    contain this change. Sending the operator to github.com instead is the
                    act ADR-079 removed from their path. */}
                {run.proposal?.improvement_warning
                  ? <span data-testid="pacing-improvement-warning">{run.proposal.improvement_warning}</span>
                  : !run.proposal?.error && run.proposal?.pr
                    && !proposalIsSettled(proposalStatus) && (
                  run.proposal.improvement_id
                    ? <Link to={`/improvements/${encodeURIComponent(run.proposal.improvement_id)}/pr`}
                            className="font-medium underline">Review &amp; approve in Mission Control →</Link>
                    : <span>Find it under <Link to="/improvements" className="font-medium underline">Improvements</Link>.</span>
                )}
                {run.proposal?.provenance_note && !run.proposal?.error && (
                  <span data-testid="pacing-provenance-note">{run.proposal.provenance_note}</span>
                )}
                {st.kind !== 'finished' && (
                  <span className="ml-auto flex items-center gap-1">
                    {/* NEXT IS OFFERED WHENEVER A TURN COULD RUN, including while paused —
                        an operator who paused to read something still wants one step. It is
                        NOT offered while a turn is in flight or a checkpoint is open, because
                        neither can be skipped past. */}
                    {(st.kind === 'counting' || st.kind === 'paused') && (
                      <button
                        type="button" disabled={busy} onClick={() => void pace('next')}
                        title="Start the next phase now, without waiting for the countdown."
                        className="rounded border border-current/30 bg-white/70 px-1.5 py-0.5 hover:bg-white disabled:opacity-50">
                        Next phase
                      </button>
                    )}
                    {st.kind === 'counting' && (
                      <button
                        type="button" disabled={busy} onClick={() => void pace('pause')}
                        title="Hold this run where it is. Nothing advances until you resume."
                        className="rounded border border-current/30 bg-white/70 px-1.5 py-0.5 hover:bg-white disabled:opacity-50">
                        Pause
                      </button>
                    )}
                    {st.kind === 'paused' && (
                      <button
                        type="button" disabled={busy} onClick={() => void pace('resume')}
                        title="Resume automatic advancing. The countdown restarts from now."
                        className="rounded border border-current/30 bg-white/70 px-1.5 py-0.5 hover:bg-white disabled:opacity-50">
                        Resume
                      </button>
                    )}
                  </span>
                )}
              </div>
            )
          })()}

          {/* WHY THIS PHASE IS SLOW, and WHAT TO DO WHEN IT STOPS.
              Two different failures on 2026-09-09, both of which the backend had already
              diagnosed and the rail did not show. One run sat 28 minutes in `results` — which
              reverts each changed file and re-runs the whole suite per file, so minutes are
              expected — under the word "Working". The other burned 16 attempts against two
              tests that failed IDENTICALLY nine times while the passing count climbed 506 to
              517, and ended on "every attempt was refused", which points at the
              implementation when the tests were the suspect.

              A verdict the backend produces and the UI does not surface is a diagnosis nobody
              receives. */}
          {run.turn_in_flight && phaseActivityNote(run.phase) && (
            <p className="mt-1 text-[11px] leading-snug text-gray-600" data-testid="phase-activity">
              {phaseActivityNote(run.phase)}
            </p>
          )}
          {stopped && (
            <p className="mt-2 rounded border border-gray-300 bg-gray-50 px-2 py-1.5 text-[11px] text-gray-700"
               data-testid="run-stopped">
              This run was stopped by {stopped.by} at {stopped.phase}. It takes no more turns.
            </p>
          )}
          {(() => {
            const g = runGuidance(run)
            if (!g) return null
            return (
              <div className="mt-2 rounded border border-amber-300 bg-amber-50 px-2 py-1.5 text-[11px] text-amber-900"
                   data-testid="run-guidance">
                <p className="font-medium" data-testid="run-guidance-headline">{g.headline}</p>
                <p className="mt-0.5" data-testid="run-guidance-action">{g.action}</p>
                {g.detail && g.detail.length > 0 && (
                  <ul className="mt-1 list-disc pl-4 font-mono text-[10px]" data-testid="run-guidance-detail">
                    {g.detail.map(d => <li key={d}>{d}</li>)}
                  </ul>
                )}
              </div>
            )
          })()}
          {/* SURFACED, not swallowed. A control that reports nothing leaves an operator
              looking at a button that did nothing — the same reason resetErr exists. */}
          {pacingErr && (
            <div className="mt-1 rounded border border-red-300 bg-red-50 px-2 py-1 text-[10px] text-red-800">
              The control was not applied — {pacingErr}
            </div>
          )}
          {/* The error belongs OUTSIDE the checkpoint card, because the reset lives
              outside it too. It used to render only inside CheckpointCard, so a failed
              reset on a run with no pending checkpoint was completely silent — a button
              that does nothing, which is the state that sends an operator to a terminal. */}
          {resetErr && (
            <div className="rounded border border-red-300 bg-red-50 p-1.5 text-[10px] text-red-800">
              The run was not cleared — {resetErr}
            </div>
          )}

          {/* THE RUN IS FINISHED AND WAITING ON YOU — ADR-178 D3, owner decision 2026-09-17.
              A run no longer proposes itself: five pull requests landed on one workflow with
              no click and all five were closed by hand. That gate is only half a change —
              without this button a finished run parks with NO WAY TO SHIP IT, which is the
              state-with-no-action ADR-167 D8 calls a design bug. */}
          {run.phase === 'done' && !run.proposal && !stopped && lastVerdictPassed(run) && (
            <div className="rounded border border-emerald-300 bg-emerald-50 p-1.5 text-[10px] text-emerald-900">
              This run is <strong>finished and waiting for you</strong> — every phase passed
              and the measurements are in. Nothing has been proposed yet.
              {canEdit && (
                <button
                  type="button" disabled={proposing}
                  onClick={async () => {
                    setProposeErr('')
                    setProposing(true)
                    // agent/workflow are THIS render's props — the slot that was clicked; a rerender makes a new closure and does not change them.
                    try {
                      const r = await api.proposeAuthoringRun(agent, workflow)
                      setRun(r.run ?? null)
                      onProposed?.(agent, workflow)
                    } catch (e) {
                      // REPORTED, NEVER SWALLOWED. This opens a pull request over the
                      // network; a button that silently does nothing is the shape this
                      // surface has been losing operators to all week.
                      setProposeErr(e instanceof Error ? e.message : 'the run could not be proposed')
                    } finally {
                      setProposing(false)
                    }
                  }}
                  className="ml-1 rounded border border-emerald-400 bg-white px-1.5 py-0.5 font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50">
                  {proposing ? 'Proposing…' : 'Propose this run'}
                </button>
              )}
              {proposeErr && (
                // The server names an opened PR it could not confirm; that one WAS proposed.
                <div className="mt-1 text-red-700">
                  {proposalOpenedButUnconfirmed(proposeErr)
                    ? 'The pull request was opened, but not confirmed'
                    : 'It was not proposed'} — {proposeErr}
                </div>
              )}
            </div>
          )}

          {/* THE WAY BACK TO EMPTY. Rendered for ANY run, not only a finished one: the
              state that stranded an operator for hours was a run stuck mid-phase behind a
              timed-out delegate, and "start over" is the only move that helps there. */}
          <div className="flex flex-wrap items-center gap-2 border-t border-gray-200 pt-1.5">
            {/* CANCEL COMES FIRST, AND IS NEVER DISABLED.
                The state an operator most needs to escape — a turn that has been running
                far too long — is the one "Start a new run" beside it refuses, because
                discarding the phase state would not stop the turn. This stops it: the run
                is marked stopped and Mission Control discards the turn's result when it
                lands. Left of reset because it is the safe one: nothing is destroyed. */}
            {!stopped && run.phase !== 'done' && (
              !confirmCancel ? (
                <button
                  type="button" disabled={busy}
                  onClick={() => setConfirmCancel(true)}
                  title={run.turn_in_flight
                    ? 'Stop this run. The turn now running will finish in the engine and its result will be discarded.'
                    : 'Stop this run. The record and its verdicts are kept.'}
                  className="rounded border border-amber-300 bg-amber-50 px-1.5 py-0.5 text-[10px] text-amber-900 hover:bg-amber-100 disabled:opacity-50">
                  Cancel run
                </button>
              ) : (
                <>
                  <span className="text-[10px] text-gray-600">
                    {run.turn_in_flight
                      ? 'Stop this run? The turn now running will finish in the engine and its result will be thrown away.'
                      : 'Stop this run? Nothing is deleted — the record and its verdicts are kept.'}
                  </span>
                  <button
                    type="button" disabled={busy} onClick={() => void cancelRun()}
                    className="rounded border border-amber-400 bg-amber-100 px-1.5 py-0.5 text-[10px] text-amber-900 hover:bg-amber-200 disabled:opacity-50">
                    Yes, stop it
                  </button>
                  <button
                    type="button" disabled={busy} onClick={() => setConfirmCancel(false)}
                    className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                    Keep going
                  </button>
                </>
              )
            )}
            {cancelErr && (
              <span className="text-[10px] text-red-700">{cancelErr}</span>
            )}
            {!confirmReset ? (
              <button
                // DISABLED WHILE A TURN IS RUNNING. Discarding the run does not stop the
                // turn — it throws away the phase state the turn is about to write into,
                // so the work continues and lands nowhere. That is the worst of the three
                // outcomes and it was one click away, next to no other control.
                type="button" disabled={busy || (!!run.turn_in_flight && !stopped)}
                onClick={() => setConfirmReset(true)}
                title={run.turn_in_flight
                  ? 'A turn is running. Discarding the run now would not stop it — the work would finish and have nowhere to land.'
                  : "Discard this run's phase state so the next phase starts a new run. Your draft and the conversation are untouched."}
                className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                Start a new run
              </button>
            ) : (
              <>
                <span className="text-[10px] text-gray-600">
                  Discard {run.verdicts?.length || 0} gate verdict(s) and{' '}
                  {run.artifacts?.claims?.length || 0} claim(s)? Your draft and the
                  conversation are not touched.
                </span>
                <button
                  type="button" disabled={busy} onClick={() => void reset()}
                  className="rounded border border-red-300 bg-red-50 px-1.5 py-0.5 text-[10px] text-red-800 hover:bg-red-100 disabled:opacity-50">
                  Discard the run
                </button>
                <button
                  type="button" disabled={busy} onClick={() => setConfirmReset(false)}
                  className="rounded border border-gray-300 bg-white px-1.5 py-0.5 text-[10px] text-gray-700 hover:bg-gray-50">
                  Keep it
                </button>
              </>
            )}
          </div>
          {/* THE MOVE THIS STATE OWES (ADR-167 D8).
              A run is one turn per phase (ADR-166 D2), so a turn that finished analysis
              leaves the run AT plan with nothing running and nothing to press. The
              Process tab said "Phase 2 of 7 — plan" and stopped; the only way forward was
              to know, and type, the sentence the CI lane sends.
              NOT ON A CANCELLED RUN: the stop is a decided checkpoint, so `pending` is empty
              and this used to render. orion 20261008T165147Z-9f2bf7 ran a 700s turn from it
              eight seconds after the cancel, and the backend threw the reply away. */}
          {onContinue && !pending && !stopped && !run.turn_in_flight && run.phase !== 'done' && (
            <div className="rounded border border-indigo-200 bg-indigo-50 p-2">
              <div className="text-[11px] text-indigo-900">
                This run is waiting at <span className="font-semibold">{run.phase}</span>.
                Each phase is its own turn.
              </div>
              <button
                type="button" disabled={busy || continuing}
                onClick={() => onContinue(continuePhasePrompt(run.phase))}
                className="mt-1 rounded bg-indigo-600 px-2 py-0.5 text-[11px] font-medium text-white hover:bg-indigo-700 disabled:opacity-50">
                {continuing ? 'Running…' : `Run ${run.phase}`}
              </button>
            </div>
          )}
          {/* ADR-200 stage 1 — a scoping checkpoint gets its own card. `key` on the round
              so a new round's questions start from fresh boxes rather than carrying over
              edits typed against the previous round's ids. */}
          {pending && (
            pending.reason === 'scoping'
              ? <ScopingCard key={run.brief_round ?? 0} cp={pending} run={run}
                  onDecideBrief={decideBrief} onKeepBrief={() => void keepBrief()}
                  onDecide={decide} busy={busy} error={decideErr} />
              : <CheckpointCard cp={pending} onDecide={decide} busy={busy} error={decideErr} />
          )}
          {/* THE SAME TREE THE RUN PAGE DRAWS (ADR-177 D3/D4).
              This used to be a bespoke pair of cards that rendered CLAIMS and TESTS and
              nothing else — so an operator who advanced a run to plan saw "plan → design
              passed" and no plan, while the same run's page listed all four steps with
              their traces. Two renderers for one object is how a surface acquires a
              second, worse account of itself; there is now one, mounted twice. */}
          {/* NO TREE HERE. WorkflowRuns renders it directly below, for the current run
              and every earlier one — the rail drawing its own copy put the same run on
              the screen twice, which is exactly the duplication moving runs onto this
              page was meant to remove. The rail keeps what is only its own: where the run
              is, and the controls that move it. */}
        </>
      )}

      {/* ADR-177 D3 — the way OUT of the drawer.
          A run is a tree with retries and expandable artifacts; this panel is roughly 40%
          of the viewport and has been trying to be a full-height surface. The rail keeps
          the one-line state and hands off, rather than growing a seventh section.

          Rendered even with no current run, because the history is the more useful thing
          to reach when this slot is empty: "no run right now" is exactly when an operator
          wants the last one. */}
      <div className="border-t border-gray-200 pt-1.5">
        {/* SCOPED TO THIS WORKFLOW, and carrying the way back.
            An unscoped link dropped the operator into every run on the fleet with no
            route home — reported as "I can't get back to the fleet editor I was looking
            at without clicking into fleet editor, finding the workflow, opening,
            selecting edit". */}
        <Link
          to={`/fleet-editor/runs?agent=${encodeURIComponent(agent)}&workflow=${encodeURIComponent(workflow)}`}
          className="text-[10px] text-indigo-700 hover:underline">
          Runs for this workflow →
        </Link>
      </div>

      {children}
      </div>
    </div>
  )
}
